import type {MyDialogFilter} from '@lib/storages/filters';
import type {Dialog, ForumTopic, MyMessage, RequestHistoryOptions, SavedDialog} from '@appManagers/appMessagesManager';
import type {MyDocument} from '@appManagers/appDocsManager';
import type {State} from '@config/state';
import type {AnyDialog} from '@lib/storages/dialogs';
import type {CustomEmojiRendererElement} from '@customEmoji/renderer';
import PopupElementTsx from '@components/popups/indexTsx';
import DialogsContextMenu from '@components/dialogsContextMenu';
import DialogsSelection from '@components/dialogsSelection';
import {PINNED_DIALOG_CLASS_NAME} from '@components/dialogsPinnedReorder';
import type {DialogsSelectionBase} from '@components/dialogsSelectionBase';
import DotRenderer from '@components/dotRenderer';
import {horizontalMenuObjArgs} from '@components/horizontalMenu';
import Scrollable from '@components/scrollable';
import {ScrollableContextValue} from '@components/scrollable2';
import {formatDateAccordingToTodayNew} from '@helpers/date';
import {IS_MOBILE_SAFARI} from '@environment/userAgent';
import {logger, LogTypes} from '@lib/logger';
import rootScope from '@lib/rootScope';
import appImManager, {AppImManager, APP_TABS} from '@lib/appImManager';
import Button from '@components/button';
import SetTransition from '@components/singleTransition';
import {MyDraftMessage} from '@appManagers/appDraftsManager';
import {MOUNT_CLASS_TO} from '@config/debug';
import Modes from '@config/modes';
import PeerTitle, {changeTitleEmojiColor} from '@components/peerTitle';
import I18n, {FormatterArguments, i18n, LangPackKey, _i18n} from '@lib/langPack';
import findUpClassName from '@helpers/dom/findUpClassName';
import findUpTag from '@helpers/dom/findUpTag';
import lottieLoader from '@lib/lottie/lottieLoader';
import wrapPhoto from '@components/wrappers/photo';
import {AppEditFolderTab} from '@components/solidJsTabs/tabs';
import appSidebarLeft from '@components/sidebarLeft';
import {attachClickEvent, simulateClickEvent} from '@helpers/dom/clickEvent';
import positionElementByIndex from '@helpers/dom/positionElementByIndex';
import replaceContent from '@helpers/dom/replaceContent';
import highlightText, {TextHighlight} from '@helpers/dom/textHighlight';
import ConnectionStatusComponent from '@components/connectionStatus';
import {renderImageFromUrlPromise} from '@helpers/dom/renderImageFromUrl';
import {fastRafPromise} from '@helpers/schedulers';
import IS_TOUCH_SUPPORTED from '@environment/touchSupport';
import handleTabSwipe from '@helpers/dom/handleTabSwipe';
import {ChatlistContacts, createChatlistContacts} from '@components/sidebarLeft/chatlistContacts';
import isInDOM from '@helpers/dom/isInDOM';
import {setSendingStatus} from '@components/sendingStatus';
import {FOLDER_ID_ALL, FOLDER_ID_ARCHIVE, NULL_PEER_ID, REAL_FOLDERS} from '@appManagers/constants';
import groupCallActiveIcon from '@components/groupCallActiveIcon';
import {ChatlistsChatlistUpdates, DialogFilter, Message, MessageMedia, MessageReplyHeader, Photo} from '@layer';
import mediaSizes from '@helpers/mediaSizes';
import appNavigationController, {NavigationItem} from '@components/appNavigationController';
import appMediaPlaybackController from '@components/appMediaPlaybackController';
import {AppManagers} from '@lib/managers';
import appSidebarRight from '@components/sidebarRight';
import choosePhotoSize from '@appManagers/utils/photos/choosePhotoSize';
import renderDialogSubtitleParts from '@components/wrappers/dialogSubtitle';
import wrapMessageForReply from '@components/wrappers/messageForReply';
import getMessageForReplyContent from '@components/wrappers/messageForReplyContent';
import wrapPeerTitle from '@components/wrappers/peerTitle';
import isMessageRestricted, {isMessageSensitive} from '@appManagers/utils/messages/isMessageRestricted';
import getMediaFromMessage from '@appManagers/utils/messages/getMediaFromMessage';
import getMessageSenderPeerIdOrName from '@appManagers/utils/messages/getMessageSenderPeerIdOrName';
import wrapStickerEmoji from '@components/wrappers/stickerEmoji';
import getProxiedManagers from '@lib/getProxiedManagers';
import deferredPromise, {CancellablePromise} from '@helpers/cancellablePromise';
import middlewarePromise from '@helpers/middlewarePromise';
import appDownloadManager from '@lib/appDownloadManager';
import groupCallsController from '@lib/calls/groupCallsController';
import callsController from '@lib/calls/callsController';
import conferenceInvitesController from '@lib/calls/conferenceInvitesController';
import cancelEvent from '@helpers/dom/cancelEvent';
import noop from '@helpers/noop';
import pause from '@helpers/schedulers/pause';
import apiManagerProxy from '@lib/apiManagerProxy';
import indexOfAndSplice from '@helpers/array/indexOfAndSplice';
import {getMiddleware, MiddlewareHelper} from '@helpers/middleware';
import getDialogMentionBadgeState from '@helpers/dialogMentionBadgeState';
import {attachRowController, createRowSortableIcon, RowMediaSizeType, type RowTsxController} from '@components/rowTsxController'
import getMessageThreadId from '@appManagers/utils/messages/getMessageThreadId';
import formatNumber from '@helpers/number/formatNumber';
import AppSharedMediaTab from '@components/sidebarRight/tabs/sharedMediaTab';
import {dispatchHeavyAnimationEvent} from '@hooks/useHeavyAnimationCheck';
import shake from '@helpers/dom/shake';
import getServerMessageId from '@appManagers/utils/messageId/getServerMessageId';
import {AppChatFoldersTab} from '@components/solidJsTabs/tabs';
import eachTimeout from '@helpers/eachTimeout';
import showSharedFolderInvitePopup from '@components/popups/sharedFolderInvite';
import showChatPreviewPopup, {chatPreviewAnchorFromDialogRow} from '@components/popups/chatPreview';
import showLimitPopup from '@components/popups/limit';
import StoriesList from '@components/stories/list';
import {render} from 'solid-js/web';
import {avatarNew} from '@components/avatarNew';
import Icon from '@components/icon';
import {isDialog, isForumTopic, isMonoforumDialog, isSavedDialog} from '@appManagers/utils/dialogs/isDialog';
import {ChatType} from '@components/chat/chatType';
import rtmpCallsController from '@lib/calls/rtmpCallsController';
import IS_LIVE_STREAM_SUPPORTED from '@environment/liveStreamSupport';
import createFolderContextMenu from '@helpers/dom/createFolderContextMenu';
import {useAppSettings} from '@stores/appSettings';
import {unwrap} from 'solid-js/store';
import wrapMediaSpoiler from '@components/wrappers/mediaSpoiler';
import type {MonoforumDialog} from '@lib/storages/monoforumDialogs';
import {renderPendingSuggestion} from '@components/sidebarLeft/pendingSuggestion';
import {renderNewAuthorization} from '@components/sidebarLeft/newAuthorization';
import {useHasFolders} from '@stores/foldersSidebar';
import {useAppState} from '@stores/appState';
import {BADGE_TRANSITION_TIME} from '@components/autonomousDialogList/constants';
import {AutonomousDialogList} from '@components/autonomousDialogList/dialogs';
import {PossibleDialog, setDialogTyping} from '@components/autonomousDialogList/base';
import {ForumTab} from '@components/forumTab/forumTab';
import findForumTabByPeerId from '@components/forumTab/findForumTabByPeerId';
import shouldOpenForumAsNavigationTab from '@components/forumTab/communityOpenMode';
import isCommunityChat from '@appManagers/utils/communities/isCommunity';
import {getCommunityPeerSubtitle} from '@components/communities/communityPeerStatus';
import {fillForumTabRegister} from '@components/forumTab/fillRegister';
import LazyLoadQueue from '@components/lazyLoadQueue';
import {fastSmoothScrollToStart} from '@helpers/fastSmoothScroll';
import ArchiveDialog, {archiveDialogTagName} from '@components/archiveDialog';
import {createArchiveDialogContextMenu} from '@components/archiveDialogContextMenu';
import {children, createRoot, untrack} from 'solid-js';
import useFolders from '@stores/folders';
import FoldersTabs from '@components/foldersTabs';
import clamp from '@helpers/number/clamp';
import confirmationPopup from '@components/confirmationPopup';
import ListenerSetter from '@helpers/listenerSetter';
import type {PopupPeerHandle} from '@components/popups/peer';
import {toastNew} from '@components/toast';
import {
  renderChatlistTopNotification,
  type ChatlistTopNotificationController
} from '@components/sidebarLeft/chatlistTopNotification';

export const DIALOG_LIST_ELEMENT_TAG = 'A';
// below this many dialogs the sidebar looks empty, so contacts are offered under the chat list
const MIN_DIALOGS_WITHOUT_CONTACTS = 10;

export function findDialogListElement(target: EventTarget) {
  return findUpTag(target, DIALOG_LIST_ELEMENT_TAG);
}

export type DialogDom = {
  avatarEl: ReturnType<typeof avatarNew>,
  captionDiv: HTMLElement,
  titleSpan: HTMLSpanElement,
  titleSpanContainer: HTMLSpanElement,
  statusSpan: HTMLSpanElement,
  lastTimeSpan: HTMLSpanElement,
  pinnedBadge?: HTMLElement,
  /** the grip a pinned row is dragged by while its list can be reordered */
  sortableIcon?: HTMLElement,
  unreadBadge?: HTMLElement,
  unreadAvatarBadge?: HTMLElement,
  callIcon?: ReturnType<typeof groupCallActiveIcon>,
  mentionsBadge?: HTMLElement,
  reactionsBadge?: HTMLElement,
  pollVotesBadge?: HTMLElement,
  lastMessageSpan: HTMLSpanElement,
  containerEl: HTMLElement,
  listEl: HTMLElement,
  subtitleEl: HTMLElement,
  mutedIcon?: HTMLElement,

  titleWrapOptions?: WrapSomethingOptions;

  /**
   * Signature of the last successfully rendered subtitle. Every dialog event
   * (read, mark unread, notify settings, filter update) runs the whole
   * `setLastMessage` pipeline, so without it the subtitle is torn down and
   * rebuilt — custom emoji, media thumbs and all — for updates that cannot
   * have changed a single character of it.
   */
  lastMessageRenderKey?: string,
  /**
   * The nodes that signature was rendered as. Plenty of other places
   * (community dialogs, search results, sidebar pickers) write their own
   * subtitle into `lastMessageSpan`, so a matching key alone does not mean the
   * rendered subtitle is still the one in the DOM.
   */
  lastMessageRenderParts?: HTMLElement[],
  /** search-result rows: the query lit up inside the rendered message text */
  textHighlight?: TextHighlight,

  setLastMessagePromise?: CancellablePromise<void>,
  setUnreadMessagePromise?: CancellablePromise<void>
};

function setPromiseMiddleware<T extends {[smth in K as K]?: CancellablePromise<void>}, K extends keyof T>(obj: T, key: K) {
  const oldPromise: CancellablePromise<void> = obj[key] as any;
  oldPromise?.reject();

  // @ts-ignore
  const deferred = obj[key] = deferredPromise<void>();
  deferred.catch(() => {}).finally(() => {
    if((obj[key] as any) === deferred) {
      delete obj[key];
    }
  });

  const middleware = middlewarePromise(() => (obj[key] as any) === deferred);
  return {deferred, middleware};
}

function disposeTextHighlight(dom: DialogDom) {
  dom.textHighlight?.dispose();
  dom.textHighlight = undefined;
}

/**
 * Whether the subtitle in the DOM is still exactly what `setLastMessage` put
 * there — anyone else writing into `lastMessageSpan` (a community dialog
 * falling back to its chat count, a search result, a sidebar picker) has to
 * invalidate the skip.
 */
function isRenderedSubtitleIntact(dom: DialogDom) {
  const parts = dom.lastMessageRenderParts;
  const {childNodes} = dom.lastMessageSpan;
  return !!parts &&
    parts.length === childNodes.length &&
    parts.every((part, idx) => part === childNodes[idx]);
}

function getFolderTitleTextColor(active: boolean) {
  return active ? 'primary-color' : 'secondary-text-color';
}

const BADGE_SIZE = 22;

const avatarSizeMap: {[k in DialogElementSize]?: number} = {
  bigger: 54,
  abitbigger: 42,
  small: 32
};

export type DialogElementSize = RowMediaSizeType;
export type AsAllChatsType = 'monoforum' | 'topics';

export type DialogElementOptions = {
  peerId: PeerId,
  rippleEnabled?: boolean,
  onlyFirstName?: boolean,
  meAsSaved?: boolean,
  avatarSize?: RowMediaSizeType,
  autonomous?: boolean,
  loadPromises?: Promise<any>[],
  fromName?: string,
  noIcons?: boolean,
  threadId?: number,
  monoforumParentPeerId?: PeerId,
  wrapOptions: WrapSomethingOptions,
  isMainList?: boolean,
  withStories?: boolean,
  controlled?: boolean,
  dontSetActive?: boolean,
  asAllChats?: AsAllChatsType,
  autoDeletePeriod?: number,
  avatarElement?: HTMLElement
};

export type DialogElementBadgeState = {
  muted: boolean,
  pinned: boolean,
  unread: boolean,
  unreadText?: string,
  unreadMention?: boolean,
  unreadAvatar: boolean,
  unreadAvatarText?: string,
  mentions: boolean,
  reactions: boolean,
  pollVotes: boolean,
  transitionDuration?: number
};

type DialogElementAppliedBadgeState = {
  [key in Parameters<DialogElement['toggleBadgeByKey']>[0]]: boolean
} & {
  hasOnlyPinnedBadge: boolean,
  unreadText?: string,
  unreadAvatarText?: string
};

export interface DialogElement extends RowTsxController {}

export class DialogElement {
  public dom: DialogDom;
  public isMainList: boolean;
  public middlewareHelper: MiddlewareHelper;
  private peerTitle: PeerTitle;
  private lastBadgeState: DialogElementAppliedBadgeState;

  constructor({
    peerId,
    rippleEnabled = true,
    onlyFirstName = false,
    meAsSaved = true,
    avatarSize = 'bigger',
    autonomous,
    loadPromises,
    fromName,
    noIcons,
    threadId,
    monoforumParentPeerId,
    wrapOptions = {},
    isMainList,
    withStories,
    controlled,
    dontSetActive,
    asAllChats,
    autoDeletePeriod,
    avatarElement
  }: DialogElementOptions) {
    const wrapMiddleware = wrapOptions?.middleware;
    this.middlewareHelper = wrapMiddleware ? wrapOptions.middleware.create() : (controlled ? getMiddleware() : undefined);

    attachRowController(this, {
      clickable: true,
      noRipple: !rippleEnabled,
      havePadding: !threadId && asAllChats !== 'topics',
      title: true,
      titleRightSecondary: true,
      subtitle: true,
      subtitleRight: true,
      noWrap: true,
      asLink: true,
      middleware: this.middlewareHelper?.get()
    });

    this.isMainList = isMainList;
    this.subtitleRight.remove();

    const newWrapOptions: WrapSomethingOptions = {
      ...wrapOptions
    };

    if(this.middlewareHelper) {
      newWrapOptions.middleware = this.middlewareHelper.get();
    }

    const isSavedDialog = !!threadId && peerId === rootScope.myId;
    const isForumTopic = !!threadId && !isSavedDialog;

    const usePeerId = isSavedDialog ? threadId : peerId;

    const avatar = avatarElement || isForumTopic || asAllChats === 'topics' ?
      undefined :
      avatarNew({
        middleware: this.middlewareHelper.get(),
        size: avatarSizeMap[avatarSize],

        lazyLoadQueue: newWrapOptions.lazyLoadQueue,
        isDialog: !!meAsSaved,
        peerId: fromName ? NULL_PEER_ID : usePeerId,
        peerTitle: fromName,
        withStories,
        // Animate video avatars only in the primary chat list (withStories), not
        // in compact pickers / search rows.
        withVideoAvatar: withStories,
        wrapOptions: newWrapOptions,
        meAsNotes: isSavedDialog,
        asAllChats: asAllChats === 'monoforum',
        autoDeletePeriod
      });
    loadPromises?.push(avatar?.readyThumbPromise);
    const avatarEl = avatarElement || avatar?.node;
    if(avatarEl) {
      avatarEl.classList.add('dialog-avatar');
      this.applyMediaElement(avatarEl, avatarSize);
    }

    const captionDiv = this.container;

    const titleSpanContainer = this.title;
    titleSpanContainer.classList.add('user-title');

    this.titleRow.classList.add('dialog-title');

    const isActive = !dontSetActive && !autonomous &&
      appImManager.chat &&
      appImManager.isSamePeer(appImManager.chat, {
        peerId: monoforumParentPeerId || peerId,
        monoforumThreadId: monoforumParentPeerId ? peerId : undefined,
        threadId: threadId,
        type: isSavedDialog ? ChatType.Saved : ChatType.Chat
      });

    const titleWrapOptions: WrapSomethingOptions = {
      textColor: appDialogsManager.getTextColor(isActive),
      ...newWrapOptions
    };

    const peerTitle = this.peerTitle = new PeerTitle();
    const peerTitlePromise = peerTitle.update({
      peerId: usePeerId,
      fromName,
      dialog: meAsSaved,
      onlyFirstName,
      withIcons: !noIcons,
      threadId: isSavedDialog ? undefined : threadId,
      wrapOptions: titleWrapOptions,
      iconsColor: appDialogsManager.getPrimaryColor(isActive),
      meAsNotes: isSavedDialog,
      asAllChats
    });

    loadPromises?.push(peerTitlePromise);
    titleSpanContainer.append(peerTitle.element);

    // const titleIconsPromise = generateTitleIcons(peerId).then((elements) => {
    //   titleSpanContainer.append(...elements);
    // });

    // if(loadPromises) {
    //   loadPromises.push(titleIconsPromise);
    // }
    // }

    const span = this.subtitle;
    // span.classList.add('user-last-message');

    const li = this.container;
    li.classList.add('chatlist-chat', 'chatlist-chat-' + avatarSize);
    if(!autonomous) {
      (li as HTMLAnchorElement).href = '#' + peerId;
    }
    // if(rippleEnabled) {
    //   ripple(li);
    // }

    if(avatarSize === 'bigger') {
      this.container.classList.add('row-big');
    } else if(avatarSize === 'small') {
      this.container.classList.add('row-small');
    }

    li.dataset.peerId = '' + peerId;

    if(threadId) li.dataset.threadId = '' + threadId;
    if(monoforumParentPeerId) li.dataset.monoforumParentPeerId = '' + monoforumParentPeerId;
    if(asAllChats) li.dataset.isAllChats = 'true';

    const statusSpan = document.createElement('span');
    statusSpan.classList.add('message-status', 'sending-status'/* , 'transition', 'reveal' */);

    const lastTimeSpan = document.createElement('span');
    lastTimeSpan.classList.add('message-time');

    const rightSpan = this.titleRight;
    rightSpan.classList.add('dialog-title-details');
    rightSpan.append(statusSpan, lastTimeSpan);

    this.subtitleRow.classList.add('dialog-subtitle', 'has-multiple-badges');

    // if(I18n.isRTL) {
    //   // this.subtitle.dir = '';
    //   // this.subtitle.dir = 'rtl';
    // }

    const dom: DialogDom = this.dom = {
      avatarEl: avatar,
      captionDiv,
      titleSpan: peerTitle.element,
      titleSpanContainer,
      statusSpan,
      lastTimeSpan,
      lastMessageSpan: span,
      containerEl: li,
      listEl: li,
      subtitleEl: this.subtitleRow,
      titleWrapOptions
    };

    // this will never happen for migrated legacy chat
    if(!autonomous) {
      (li as any).dialogDom = dom;

      if(isMainList && !asAllChats && appDialogsManager.forumTab?.peerId === peerId && !threadId) {
        li.classList.add('is-forum-open');
      }
    }

    if(isActive) {
      appDialogsManager.setDialogActive(li, true);
    }
  }

  public destroy() {
    this.dispose();
    this.middlewareHelper?.destroy();
    disposeTextHighlight(this.dom);
  }

  public updateTitle(fromName: string) {
    return this.peerTitle.update({fromName});
  }

  public remove() {
    this.destroy();
    this.dom.listEl.remove();
  }

  public setMuted(isMuted: boolean, transitionDuration = 0) {
    const {dom} = this;
    const wasMuted = dom.listEl.classList.contains('is-muted') &&
      !dom.listEl.classList.contains('backwards');
    if(isMuted === wasMuted) {
      return;
    }

    if(isMuted && !dom.mutedIcon) {
      dom.mutedIcon = Icon('nosound_filled', 'dialog-muted-icon');
      dom.titleSpanContainer.append(dom.mutedIcon);
    }

    SetTransition({
      element: dom.listEl,
      className: 'is-muted',
      forwards: isMuted,
      duration: transitionDuration,
      onTransitionEnd: !isMuted ? (() => {
        dom.mutedIcon?.remove();
        delete dom.mutedIcon;
      }) : undefined
    });
  }

  public createPinnedBadge() {
    if(this.dom.pinnedBadge) return;
    const badge = this.dom.pinnedBadge = document.createElement('div');
    badge.className = `dialog-subtitle-badge badge badge-icon badge-${BADGE_SIZE} dialog-subtitle-badge-pinned`;
    badge.append(Icon('chatspinned'));
    this.dom.subtitleEl.append(badge);
  }

  /** The grip a pinned row is dragged by, shown only while its list can be reordered */
  public createSortableIcon() {
    if(this.dom.sortableIcon) return;
    const icon = this.dom.sortableIcon = createRowSortableIcon();
    this.dom.listEl.append(icon);
  }

  public createUnreadBadge() {
    if(this.dom.unreadBadge) return;
    const badge = this.dom.unreadBadge = document.createElement('div');
    badge.className = `dialog-subtitle-badge badge badge-${BADGE_SIZE} dialog-subtitle-badge-unread`;
    this.dom.subtitleEl.append(badge);
  }

  public createUnreadAvatarBadge() {
    if(this.dom.unreadAvatarBadge) return;
    const badge = this.dom.unreadAvatarBadge = document.createElement('div');
    badge.className = `dialog-subtitle-badge badge badge-${BADGE_SIZE} avatar-badge`;
    this.dom.listEl.append(badge);
  }

  public createMentionsBadge() {
    if(this.dom.mentionsBadge) return;
    const badge = this.dom.mentionsBadge = document.createElement('div');
    badge.className = `dialog-subtitle-badge badge badge-${BADGE_SIZE} mention mention-badge dialog-subtitle-badge-mention`;
    badge.innerText = '@';
    this.dom.subtitleEl.append(badge);
  }

  public createReactionsBadge() {
    if(this.dom.reactionsBadge) return;
    const badge = this.dom.reactionsBadge = document.createElement('div');
    badge.className = `dialog-subtitle-badge badge badge-${BADGE_SIZE} reaction-badge dialog-subtitle-badge-reaction`;
    badge.append(Icon('reactions_filled'));
    this.dom.subtitleEl.append(badge);
  }

  public createPollVotesBadge() {
    if(this.dom.pollVotesBadge) return;
    const badge = this.dom.pollVotesBadge = document.createElement('div');
    badge.className = `dialog-subtitle-badge badge badge-${BADGE_SIZE} poll-vote-badge dialog-subtitle-badge-pollvote`;
    badge.append(Icon('poll'));
    this.dom.subtitleEl.append(badge);
  }

  public setBadgeState(options: DialogElementBadgeState) {
    const transitionDuration = options.transitionDuration || 0;
    this.setMuted(options.muted, transitionDuration);

    const previous = this.lastBadgeState;

    const mounted = {
      pinnedBadge: !!this.dom.pinnedBadge,
      unreadBadge: !!this.dom.unreadBadge,
      unreadAvatarBadge: !!this.dom.unreadAvatarBadge,
      mentionsBadge: !!this.dom.mentionsBadge,
      reactionsBadge: !!this.dom.reactionsBadge,
      pollVotesBadge: !!this.dom.pollVotesBadge
    };
    // * the pinned rows of a list form the block that can be reordered within itself, and
    // * `Sortable` reads that block off the DOM - see attachPinnedDialogsReorder
    this.dom.listEl.classList.toggle(PINNED_DIALOG_CLASS_NAME, !!options.pinned);

    if(options.pinned) {
      this.createPinnedBadge();
      this.createSortableIcon();
    }

    if(options.unread) this.createUnreadBadge();
    if(options.unreadAvatar) this.createUnreadAvatarBadge();
    if(options.mentions) this.createMentionsBadge();
    if(options.reactions) this.createReactionsBadge();
    if(options.pollVotes) this.createPollVotesBadge();

    const subtitleBadgesLength = [
      options.pinned,
      options.unread,
      options.mentions,
      options.reactions,
      options.pollVotes
    ].filter(Boolean).length;
    const hasOnlyPinnedBadge = options.pinned && subtitleBadgesLength === 1;
    // * `SetTransition` keeps `animating` on the element for the whole duration,
    // * and `.has-only-pinned-badge:not(.animating)` drops the subtitle's trailing
    // * margin while it is there. Replaying it for an unchanged state makes the
    // * subtitle of every pinned row widen and snap back — a visible blink.
    if(!previous || previous.hasOnlyPinnedBadge !== hasOnlyPinnedBadge) {
      SetTransition({
        element: this.subtitleRow,
        className: 'has-only-pinned-badge',
        forwards: hasOnlyPinnedBadge,
        duration: transitionDuration
      });
    }

    const states: Array<[
      Parameters<DialogElement['toggleBadgeByKey']>[0],
      boolean
    ]> = [
      ['pinnedBadge', options.pinned],
      ['unreadBadge', options.unread],
      ['unreadAvatarBadge', options.unreadAvatar],
      ['mentionsBadge', options.mentions],
      ['reactionsBadge', options.reactions],
      ['pollVotesBadge', options.pollVotes]
    ];
    for(const [key, visible] of states) {
      if(!this.dom[key]) {
        continue;
      }

      // an already mounted badge that keeps its visibility needs no transition
      if(mounted[key] && previous?.[key] === visible) {
        continue;
      }

      this.toggleBadgeByKey(key, visible, mounted[key], !transitionDuration);
    }

    if(options.unread && this.dom.unreadBadge) {
      if(options.unreadText !== undefined && options.unreadText !== previous?.unreadText) {
        this.dom.unreadBadge.innerText = options.unreadText;
        if(this.dom.unreadAvatarBadge) {
          this.dom.unreadAvatarBadge.innerText = options.unreadText;
        }
      }
      this.dom.unreadBadge.classList.add('unread');
      this.dom.unreadBadge.classList.toggle('mention', !!options.unreadMention);
    }

    if(options.unreadAvatar && this.dom.unreadAvatarBadge) {
      if(options.unreadAvatarText !== undefined && options.unreadAvatarText !== previous?.unreadAvatarText) {
        this.dom.unreadAvatarBadge.innerText = options.unreadAvatarText;
      }
      this.dom.unreadAvatarBadge.classList.add('unread');
      this.dom.unreadAvatarBadge.classList.toggle('mention', !!options.unreadMention);
    }

    this.lastBadgeState = {
      pinnedBadge: options.pinned,
      unreadBadge: options.unread,
      unreadAvatarBadge: options.unreadAvatar,
      mentionsBadge: options.mentions,
      reactionsBadge: options.reactions,
      pollVotesBadge: options.pollVotes,
      hasOnlyPinnedBadge,
      unreadText: options.unreadText,
      unreadAvatarText: options.unreadAvatarText
    };
  }

  public toggleBadgeByKey(
    key: Extract<keyof DialogDom, 'unreadBadge' | 'unreadAvatarBadge' | 'mentionsBadge' | 'reactionsBadge' | 'pollVotesBadge' | 'pinnedBadge'>,
    hasBadge: boolean,
    justCreated: boolean,
    batch?: boolean
  ) {
    // * `setBadgeState` skips a badge whose visibility `lastBadgeState` says is unchanged, so
    // * out-of-band toggles (`toggleAvatarUnreadBadges` on sidebar collapse / forum tab) must
    // * keep it in sync — otherwise reading the last unread topic can't hide the avatar badge
    if(this.lastBadgeState) {
      this.lastBadgeState[key] = hasBadge;
    }

    SetTransition({
      element: this.dom[key],
      className: 'is-visible',
      forwards: hasBadge,
      duration: batch ? 0 : BADGE_TRANSITION_TIME,
      onTransitionEnd: hasBadge ? undefined : () => {
        this.dom[key].remove();
        delete this.dom[key];
      },
      useRafs: !justCreated || !isInDOM(this.dom[key]) ? 2 : 0
    });
  }
}

// const testScroll = false;
// let testTopSlice = 1;

type FilterRendered = {
  id: number,
  container: HTMLElement,
  scrollable: Scrollable,
  topNotification?: ChatlistTopNotificationController,
  topNotificationContainer?: HTMLElement,
  topNotificationData?: {
    _: 'chatlistUpdates',
    chatlistUpdates: ChatlistsChatlistUpdates
  }
};

type GetDialogOptions = {
  threadOrSavedId?: number;
  monoforumParentPeerId?: number;
};

type InitDialogAdditionalOptions = {
  isBatch?: boolean,
  dialog?: AnyDialog,
  lastMessage?: MyMessage,
  onInitPromise?: (promise: Promise<unknown>) => void
};

const TEST_TOP_NOTIFICATION = true ? undefined : (): ChatlistsChatlistUpdates => ({
  _: 'chatlists.chatlistUpdates',
  chats: [],
  users: [],
  missing_peers: [{
    _: 'peerUser',
    user_id: rootScope.myId.toUserId()
  }]
});


/**
 * Which selection governs which chat list. The main list and the archive share one (the chats of
 * whichever folder is on screen); an open forum tab has its own, for its topics - so a row is asked
 * about through the list it is in rather than through one selection everybody knows.
 */
const SELECTION_BY_LIST: WeakMap<HTMLElement, DialogsSelectionBase> = new WeakMap();

export class AppDialogsManager {
  public chatsContainer = document.getElementById('chatlist-container') as HTMLDivElement;

  /** Selecting several chats at once and acting on all of them, from the list that is on screen */
  public selection = new DialogsSelection({
    managers: rootScope.managers,
    // * the bar stands in for the header of whichever sidebar tab the list on screen lives in -
    // * the main one, the archive - so it is taken from that list rather than looked up by name
    getHeader: () => {
      const tab = findUpClassName(this.xd?.sortedList?.list, 'sidebar-slider-item') ||
        appSidebarLeft.sidebarEl.querySelector('.item-main');
      return tab?.querySelector('.sidebar-header');
    },
    getFilterId: () => this.filterId,
    getSortedList: () => this.xd?.sortedList,
    getDialogKey: (element) => this.xd?.getDialogKeyFromElement(element),
    isListNarrow: () => this.isChatListNarrow(),
    // the archive is a tab of its own beside the main one, so the drag is listened for over both
    listContainer: this.chatsContainer?.closest<HTMLElement>('.sidebar-slider')
  });

  private log = logger('DIALOGS', LogTypes.Log | LogTypes.Error | LogTypes.Warn | LogTypes.Debug);

  public contextMenu: DialogsContextMenu;

  public filterId: number;
  public folders: {[k in 'menu' | 'container' | 'menuScrollContainer' | 'menuGradient']: HTMLElement} = {
    menu: undefined,
    menuScrollContainer: undefined,
    menuGradient: undefined,
    container: document.getElementById('folders-container')
  };
  private filtersRendered: {
    [filterId: string]: FilterRendered
  } = {};
  private showFiltersPromise: Promise<void>;

  private lastActiveElements: Set<HTMLElement> = new Set();

  private contactsPlaceholder: ChatlistContacts;

  private initedListeners = false;

  public onListLengthChange: () => Promise<void>;
  private allChatsIntlElement: I18n.IntlElement;

  private emptyDialogsPlaceholderSubtitle: I18n.IntlElement;
  private updateContactsLengthPromise: Promise<number>;

  private filtersNavigationItem: NavigationItem;

  private managers: AppManagers;

  public doNotRenderChatList: boolean;
  public isFirstDialogsLoad: boolean;

  private stateMiddlewareHelper: MiddlewareHelper;

  private forumsTabs: Map<PeerId, ForumTab>;
  private forumsSlider: HTMLElement;
  private forumTabByPeerIdPromises = new Map<PeerId, Promise<void>>();
  public forumTab: ForumTab;
  private forumNavigationItem: NavigationItem;

  public xd: AutonomousDialogList;
  public xds: {[filterId: number]: AutonomousDialogList} = {};

  public cancelChatlistUpdatesFetching: () => void;
  public fetchChatlistUpdates: () => void;

  private storiesListContainer: HTMLDivElement;
  private bottomPart: HTMLDivElement;
  private disposeStories: () => void;
  public resizeStoriesList: () => void;

  private suggestionContainer: HTMLElement;
  private authorizationContainer: HTMLElement;
  private foldersOverlay: HTMLElement;

  private lazyLoadQueue: LazyLoadQueue;

  public start() {
    const managers = this.managers = getProxiedManagers();

    this.contextMenu = new DialogsContextMenu(managers);
    this.stateMiddlewareHelper = getMiddleware();
    this.lazyLoadQueue = new LazyLoadQueue(5, true);

    // this.onListLengthChange = debounce(this._onListLengthChange, 100, false, true);
    this.onListLengthChange = () => void this._onListLengthChange();

    const bottomPart = this.bottomPart = document.createElement('div');
    bottomPart.classList.add('connection-status-bottom');
    bottomPart.append(this.folders.container);

    // Single absolute overlay sitting above the chatlist (#folders-container) that hosts every
    // panel currently rendered there: pending suggestion, folder tabs scrollable, gradient.
    // The panels flow naturally inside; only the overlay itself is absolutely positioned, so a
    // new panel above the tabs just stacks instead of overlapping (see _leftSidebar.scss).
    this.foldersOverlay = document.createElement('div');
    this.foldersOverlay.classList.add('chatlist-overlay');
    bottomPart.prepend(this.foldersOverlay);

    // Surface the overlay's live height to the parent so the chatlist below can leave matching
    // top padding — .folders-scrollable reads var(--chatlist-overlay-height) for its padding-top.
    new ResizeObserver((entries) => {
      const height = entries[0].borderBoxSize?.[0]?.blockSize ?? entries[0].contentRect.height;
      bottomPart.style.setProperty('--chatlist-overlay-height', height + 'px');
    }).observe(this.foldersOverlay);

    const storiesListContainer = this.storiesListContainer = document.createElement('div');
    storiesListContainer.classList.add('stories-list');

    fillForumTabRegister();

    this.forumsTabs = new Map();
    this.forumsSlider = document.createElement('div');
    this.forumsSlider.classList.add('topics-slider');

    this.chatsContainer.parentElement.parentElement.append(this.forumsSlider);

    if(IS_TOUCH_SUPPORTED) {
      handleTabSwipe({
        element: this.folders.container,
        onSwipe: (xDiff) => {
          const folders = useFolders();
          const prevIndex = folders.selectedFolderIndex();
          const newIndex = clamp(
            xDiff < 0 ? prevIndex + 1 : prevIndex - 1,
            0,
            folders.folderItems.length - 1
          );
          folders.onClick()(newIndex);
        },
        verifyTouchTarget: () => {
          return !this.forumTab;
        }
      });
    }

    this.allChatsIntlElement = new I18n.IntlElement({
      key: 'FilterAllChatsShort'
    });

    rootScope.addEventListener('state_cleared', () => {
      const clearCurrent = REAL_FOLDERS.has(this.filterId);
      this.xd.loadedDialogsAtLeastOnce = false;
      this.isFirstDialogsLoad = true;
      this.showFiltersPromise = undefined;

      if(clearCurrent) {
        this.xd.clear();
        this.onTabChange();
      }

      this.onStateLoaded(useAppState()[0]);
    });

    createRoot(() => {
      let scrollableContext: ScrollableContextValue;
      const element = FoldersTabs({
        scrollableProps: {
          class: 'folders-tabs-scrollable hide',
          ref: (ref) => {
            this.folders.menuScrollContainer = ref;
          },
          scrollableProps: {
            contextRef: (ref) => scrollableContext = ref
          }
        },
        menuProps: {
          id: 'folders-tabs',
          ref: (ref) => {
            this.folders.menu = ref;
            this.onRef(scrollableContext);
          }
        },
        gradientProps: {
          className: 'folders-tabs-gradient',
          color: 'surface',
          smaller: true,
          ref: (ref) => {
            this.folders.menuGradient = ref;
            ref.classList.add('hide');
          }
        }
      });

      const resolvedChildren = children(() => element).toArray();
      this.foldersOverlay.append(...resolvedChildren as any);
    });

    const [appState] = useAppState();
    const [appSettings, setAppSettings] = useAppSettings();
    // * it should've had a better place :(
    appMediaPlaybackController.setPlaybackParams(unwrap(appSettings.playbackParams));
    // Persist the normalized params back once — `setPlaybackParams` clamps a stale >1
    // `volume` to [0, 1] and migrates the excess into the voice-only `boost`, so this
    // rewrites a corrupted stored value (e.g. volume: 1.04) instead of waiting for a change.
    setAppSettings('playbackParams', appMediaPlaybackController.getPlaybackParams());
    appMediaPlaybackController.addEventListener('playbackParams', (params) => {
      setAppSettings('playbackParams', params);
    });

    mediaSizes.addEventListener('resize', () => {
      this.changeFiltersAllChatsKey();
    });

    this.chatsContainer.append(bottomPart);

    setTimeout(() => {
      lottieLoader.loadLottieWorkers();
    }, 200);

    PopupElementTsx.MANAGERS = rootScope.managers = managers;
    appDownloadManager.construct(managers);
    appDownloadManager.showPollCancelConfirmation = (randomId: string) => this.showPollCancelConfirmation(randomId);
    appSidebarLeft.construct(managers);
    appSidebarRight.construct(managers);
    groupCallsController.construct(managers);
    callsController.construct(managers);
    conferenceInvitesController.construct(managers);
    appImManager.construct(managers);
    if(IS_LIVE_STREAM_SUPPORTED) rtmpCallsController.construct(managers);
    new ConnectionStatusComponent().construct(managers, this.chatsContainer, appSidebarLeft.inputSearch);

    // start

    this.xd = this.xds[this.filterId];

    appSidebarLeft.onCollapsedChange();
    this.onStateLoaded(appState);
  }

  private onRef(scrollableContext: ScrollableContextValue) {
    this.setFilterId(FOLDER_ID_ALL);
    this.addFilter({
      id: FOLDER_ID_ALL,
      title: {_: 'textWithEntities', text: '', entities: []},
      localId: FOLDER_ID_ALL
    });

    const {setSelectedFolderId, onClick, setOnClick, folderItems} = useFolders();
    const selectFolderByIndex = async(index: number) => {
      const id = folderItems[index]?.filter.id ?? FOLDER_ID_ALL;
      const wasFilterId = this.filterId;

      const available = wasFilterId === -1 ||
        REAL_FOLDERS.has(id) ||
        (await rootScope.managers.filtersStorage.isFilterIdAvailable(id) ?? true);
      if(!available) {
        showLimitPopup('folders');
        return false;
      }

      // Switching folders clears whatever is open in the left sidebar — an open
      // global search and any stacked tabs — mirroring how opening a left-sidebar
      // tab does it (closeEverythingInside). Both folder UIs (horizontal tabs and
      // the vertical folders sidebar) funnel through here. Tabs are closed the
      // natural way (as if via the back arrow), so a tab that needs confirmation
      // still asks; if the user declines, cancel the folder switch entirely.
      if(!await appSidebarLeft.closeEverythingInsideNaturally()) {
        return false;
      }

      if(!IS_MOBILE_SAFARI) {
        if(index) {
          if(!this.filtersNavigationItem) {
            this.filtersNavigationItem = {
              type: 'filters',
              onPop: () => {
                onClick()(0);
                this.filtersNavigationItem = undefined;
              }
            };

            appNavigationController.spliceItems(1, 0, this.filtersNavigationItem);
          }
        } else if(this.filtersNavigationItem) {
          appNavigationController.removeItem(this.filtersNavigationItem);
          this.filtersNavigationItem = undefined;
        }
      }

      if(wasFilterId === id) {
        fastSmoothScrollToStart(this.xds[id].scrollable.container, 'y');
        return;
      }

      setSelectedFolderId(id);

      this.xds[id].clear();
      const promise = this.setFilterIdAndChangeTab(id);
      if(wasFilterId !== -1) {
        return promise;
      }
    };

    this.foldersOverlay.append(this.folders.menuScrollContainer);
    const selectTab = horizontalMenuObjArgs({
      tabs: this.folders.menu,
      content: this.folders.container,
      onClick: selectFolderByIndex,
      onTransitionEnd: () => {
        for(const folderId in this.xds) {
          if(+folderId !== this.filterId) {
            this.xds[folderId].clear();
          }
        }
      },
      scrollableX: scrollableContext,
      onChange: ({element, active}) => {
        const renderer: CustomEmojiRendererElement = element?.querySelector('custom-emoji-renderer-element');
        renderer?.setTextColor(getFolderTitleTextColor(active));
      }
    });

    setOnClick(() => selectTab);

    createFolderContextMenu({
      appSidebarLeft,
      AppChatFoldersTab,
      AppEditFolderTab,
      managers: this.managers,
      className: 'menu-horizontal-div-item',
      listenTo: this.folders.menu
    });
  }

  private _renderStories() {
    this.chatsContainer.parentElement.parentElement.firstElementChild.after(this.storiesListContainer);
    return StoriesList({
      foldInto: document.querySelector('.item-main .input-search input'),
      setScrolledOn: this.chatsContainer,
      getScrollable: () => this.xd.scrollable.container,
      listenWheelOn: this.bottomPart,
      offsetX: -1,
      resizeCallback: (callback) => {
        this.resizeStoriesList = callback;
      },
      onExpand: () => {
        const container = this.xd.scrollable.container;
        container.classList.add('scrolled-start');
        fastSmoothScrollToStart(container, 'y');
      }
    });
  }

  private renderStories() {
    this.disposeStories = render(() => this._renderStories(), this.storiesListContainer);
  }

  public get chatList() {
    return this.xd.sortedList.list;
  }

  /**
   * Whether the chat list is down to its avatars - a forum tab is open over it, or the sidebar is
   * collapsed. The rows then carry their unread count on the avatar, and cannot be selected: they
   * could not be told apart, nor open the lane a selected row makes for its checkbox.
   */
  public isChatListNarrow() {
    return !!this.forumTab || appSidebarLeft.isCollapsed();
  }

  /** Ends a selection of chats that the list has narrowed under (see `isChatListNarrow`) */
  public onChatListNarrowChange() {
    if(this.selection.isSelecting && this.isChatListNarrow()) {
      this.selection.cancelSelection();
    }
  }

  /** The selection a row belongs to, or nothing when its list has none (a picker, a panel) */
  public getSelectionForRow(row: HTMLElement) {
    const list = row?.parentElement;
    return list ? SELECTION_BY_LIST.get(list) : undefined;
  }

  public setFilterId(filterId: number) {
    // * the selection belongs to the list it was made in, and every action it offers is about that
    // * list (pinning, archiving), so it does not survive a move to another one
    if(filterId !== this.filterId) {
      this.selection.cancelSelection();
    }

    this.filterId = filterId;
  }

  public async setFilterIdAndChangeTab(filterId: number) {
    this.setFilterId(filterId);
    return this.onTabChange();
  }

  private initListeners() {
    rootScope.addEventListener('folder_unread', async(folder) => {
      if(folder.id < 0) {
        const dialogElement = this.xd.getDialogElement(folder.id);
        if(!dialogElement) {
          return;
        }

        this.setUnreadMessagesN({
          dialog: await this.managers.dialogsStorage.getDialogOnly(folder.id),
          dialogElement
        });
      }
    });

    rootScope.addEventListener('contacts_update', (userId) => {
      this.processContact(userId.toPeerId());
    });

    appImManager.addEventListener('peer_changed', ({peerId, threadId, monoforumThreadId, isForum}) => {
      const options: Parameters<AppImManager['isSamePeer']>[0] = {
        peerId,
        monoforumThreadId,
        threadId: isForum || rootScope.myId ? threadId : undefined
      };

      const getOptionsForElement = (element: HTMLElement) => {
        const elementThreadId = +element?.dataset?.threadId || undefined;
        const elementPeerId = element?.dataset?.peerId?.toPeerId();
        const monoforumParentPeerId = +element?.dataset?.monoforumParentPeerId || undefined;

        return {
          peerId: monoforumParentPeerId || elementPeerId,
          threadId: elementThreadId,
          monoforumThreadId: monoforumParentPeerId ? elementPeerId : undefined
        };
      };

      for(const element of this.lastActiveElements) {
        if(!appImManager.isSamePeer(getOptionsForElement(element), options)) {
          this.setDialogActive(element, false);
        }
      }

      const dialogElements = [
        this.xd?.getDialogElement(peerId),
        this.forumTab?.xd?.getDialogElement(
          threadId || monoforumThreadId || peerId
        )
      ].filter(Boolean);

      dialogElements.forEach(dialogElement => {
        const element = dialogElement?.dom?.listEl;
        if(!element) return;

        const optionsForElement = getOptionsForElement(element);
        if(
          appImManager.isSamePeer(optionsForElement, options) ||
          apiManagerProxy.isBotforum(optionsForElement.peerId) && optionsForElement.peerId === options.peerId
        ) {
          this.setDialogActive(element, true);
        }
      });
      // this.log('peer_changed total time:', performance.now() - perf);
    });

    rootScope.addEventListener('filter_update', async(filter) => {
      if(REAL_FOLDERS.has(filter.id)) {
        return;
      }

      if(!this.filtersRendered[filter.id]) {
        this.addFilter(filter);
      }
    });

    rootScope.addEventListener('filter_delete', (filter) => {
      const elements = this.filtersRendered[filter.id];
      if(!elements) return;

      elements.topNotification?.dispose();
      elements.container.remove();

      this.xds[filter.id].destroy();
      delete this.xds[filter.id];
      delete this.filtersRendered[filter.id];

      this.onFiltersLengthChange();
    });

    rootScope.addEventListener('filter_order', async(order) => {
      order = order.slice();
      indexOfAndSplice(order, FOLDER_ID_ARCHIVE);

      const r = await Promise.all(order.map(async(filterId) => {
        const [indexKey, filter] = await Promise.all([
          this.managers.dialogsStorage.getDialogIndexKeyByFilterId(filterId),
          this.managers.filtersStorage.getFilter(filterId)
        ]);

        return {indexKey, filter};
      }));

      order.forEach((filterId, idx) => {
        const {indexKey, filter} = r[idx];
        const renderedFilter = this.filtersRendered[filterId];

        this.xds[filterId].setIndexKey(indexKey);

        positionElementByIndex(renderedFilter.container, this.folders.container, filter.localId);
      });
    });
  }

  public getTextColor(active: boolean) {
    return active ? 'white' : 'secondary-text-color';
  }

  public getPrimaryColor(active: boolean) {
    return active ? 'white' : 'primary-color';
  }

  public setDialogActiveStatus(listEl: HTMLElement, active: boolean) {
    listEl.classList.toggle('active', active);

    const customEmojiRenderers = listEl.querySelectorAll<CustomEmojiRendererElement>('.custom-emoji-renderer');
    customEmojiRenderers.forEach((customEmojiRenderer) => {
      customEmojiRenderer.setTextColor(this.getTextColor(active));
    });

    const dom = (listEl as any).dialogDom as DialogDom;
    if(dom?.titleWrapOptions) {
      dom.titleWrapOptions.textColor = this.getTextColor(active);
    }

    changeTitleEmojiColor(listEl, this.getPrimaryColor(active));

    // the subtitle's text spoilers are painted on a canvas — CSS can't recolor them
    DotRenderer.setInlineSpoilersTextColor(listEl, this.getTextColor(active));
  }

  public setDialogActive(listEl: HTMLElement, active: boolean) {
    const dom = (listEl as any).dialogDom as DialogDom;
    this.setDialogActiveStatus(listEl, active);
    listEl.classList.toggle(
      'is-forum-open',
      this.forumTab?.peerId === listEl.dataset.peerId.toPeerId() &&
        !listEl.dataset.threadId &&
        !listEl.dataset.isAllChats);
    if(active) {
      this.lastActiveElements.add(listEl);
    } else {
      this.lastActiveElements.delete(listEl);
    }

    if(dom?.callIcon) {
      dom.callIcon.setActive(active);
    }
  }

  private async onStateLoaded(state: State) {
    this.stateMiddlewareHelper.clean();
    const middleware = this.stateMiddlewareHelper.get();
    const filtersArr = state.filtersArr;
    const haveFilters = filtersArr.length > REAL_FOLDERS.size;
    // const filter = filtersArr.find((filter) => filter.id !== FOLDER_ID_ARCHIVE);

    this.disposeStories?.();
    this.disposeStories =
      this.resizeStoriesList =
      undefined;

    const {onClick: _onClick, hydrateFilters} = useFolders();
    const onClick = untrack(_onClick);

    const addFilters = async(filters: MyDialogFilter[]) => {
      for(const filter of filters) {
        this.addFilter(filter);
      }

      await untrack(() => hydrateFilters(filters));
    };

    let addFiltersPromise: Promise<any>;
    if(haveFilters) {
      await addFilters(filtersArr);
    } else {
      addFiltersPromise = this.managers.filtersStorage.getDialogFilters().then(addFilters);
    }

    this.doNotRenderChatList = true;
    this.isFirstDialogsLoad = true;

    const wrapPromiseWithMiddleware = middlewarePromise(middleware);
    try {
      await wrapPromiseWithMiddleware(this.xd.preloadDialogs());
    } catch(err) {

    }

    // show the placeholder before the filters, and then will reset to the default tab again
    if(!haveFilters) {
      onClick(0, false);
    }

    addFiltersPromise && await wrapPromiseWithMiddleware(addFiltersPromise);

    this.renderStories();
    this.doNotRenderChatList = undefined;

    this.filterId = -1;
    onClick(0, false);

    if(!this.initedListeners) {
      this.initListeners();
      this.initedListeners = true;
    }

    haveFilters && this.showFiltersPromise && await wrapPromiseWithMiddleware(this.showFiltersPromise);

    this.managers.appNotificationsManager.getNotifyPeerTypeSettings();

    // await (await m(loadDialogsPromise)).renderPromise.catch(noop);
    this.managers.appMessagesManager.fillConversations();

    if(!this.suggestionContainer) {
      this.suggestionContainer = document.createElement('div');
      this.foldersOverlay.prepend(this.suggestionContainer);
      renderPendingSuggestion(this.suggestionContainer);
    }

    if(!this.authorizationContainer) {
      this.authorizationContainer = document.createElement('div');
      this.foldersOverlay.prepend(this.authorizationContainer);
      renderNewAuthorization(this.authorizationContainer);
    }
  }

  public onTabChange = () => {
    const {filterId} = this;
    this.xd = this.xds[filterId];
    this.xd.reset();

    this.cancelChatlistUpdatesFetching?.();
    this.cancelChatlistUpdatesFetching = undefined;
    this.fetchChatlistUpdates = undefined;

    const promise = this.xd.onChatsScroll();

    if(!REAL_FOLDERS.has(filterId)) {
      Promise.all([
        this.managers.filtersStorage.getFilter(filterId),
        this.managers.apiManager.getAppConfig()
        // promise.then(({renderPromise}) => renderPromise).catch(() => {})
      ]).then(([filter, appConfig]) => {
        if(TEST_TOP_NOTIFICATION ? false : filter?._ !== 'dialogFilterChatlist' || this.filterId !== filterId) {
          return;
        }

        const updatePeriod = (appConfig.chatlist_update_period ?? 3600) * 1000;
        const filterRendered = this.filtersRendered[filterId];
        let updatedTime = filterRendered.topNotification && !TEST_TOP_NOTIFICATION ?
          (filter as DialogFilter.dialogFilterChatlist).updatedTime || 0 :
          0;
        let fetching = false;

        this.fetchChatlistUpdates = () => {
          updatedTime = Date.now();

          if(fetching) {
            return;
          }

          fetching = true;
          const promise = TEST_TOP_NOTIFICATION ?
            Promise.resolve(TEST_TOP_NOTIFICATION()) :
            this.managers.filtersStorage.getChatlistUpdates(filterId);
          promise.catch(() => undefined as ChatlistsChatlistUpdates)
          .then((chatlistUpdates) => {
            if(this.filterId !== filterId || this.filtersRendered[filterId] !== filterRendered) {
              return;
            }

            const length = chatlistUpdates ? chatlistUpdates.missing_peers.length : 0;
            if(length) {
              this.createTopNotification(filterRendered);
              filterRendered.topNotificationData = {
                _: 'chatlistUpdates',
                chatlistUpdates
              };

              const topNotification = filterRendered.topNotification;

              const tt = i18n('ChatsNew', [length]);
              tt.classList.add('primary');
              const t = i18n('ChatList.SharedFolder.Title', [tt]);
              topNotification.setTitle(t);
              topNotification.setSubtitle(i18n('ChatList.SharedFolder.Subtitle'));
            }

            this.toggleTopNotification(filterRendered, !!length);
          }).finally(() => {
            fetching = false;
          });
        };

        this.cancelChatlistUpdatesFetching = eachTimeout(this.fetchChatlistUpdates, () => {
          const elapsedTime = Date.now() - updatedTime;
          return updatePeriod - elapsedTime;
        }, false);
      });
    }

    return promise;
  };

  public l(filter: Parameters<AppDialogsManager['addFilter']>[0]) {
    const xd = this.xds[filter.id] = new AutonomousDialogList({filterId: filter.id, appDialogsManager: this});
    const {scrollable, list} = xd.generateScrollable(filter);
    this.setListClickListener({list, onFound: null, withContext: true, withArchiveContext: filter.id === FOLDER_ID_ALL});

    return {ul: list, xd, scrollable};
  }

  private createTopNotification(filterRendered: FilterRendered) {
    if(filterRendered.topNotification) return;

    const topNotificationContainer = filterRendered.topNotificationContainer = document.createElement('div');
    topNotificationContainer.classList.add('chatlist-top-notification-container');
    filterRendered.topNotification = renderChatlistTopNotification(topNotificationContainer, {
      onClick: async() => {
        const data = filterRendered.topNotificationData;
        if(data._ === 'chatlistUpdates') {
          showSharedFolderInvitePopup({
            chatlistInvite: {
              ...data.chatlistUpdates,
              already_peers: [],
              _: 'chatlists.chatlistInviteAlready',
              filter_id: filterRendered.id
            },
            updating: true
          });
        }
      },
      contextMenu: {
        buttons: [{
          icon: 'hide',
          text: 'HideAboveTheList',
          onClick: () => {
            this.managers.filtersStorage.hideChatlistUpdates(filterRendered.id).then(() => {
              if(this.filterId === filterRendered.id) {
                this.fetchChatlistUpdates?.();
              }
            });
          },
          verify: () => filterRendered.topNotificationData?._ === 'chatlistUpdates'
        }]
      }
    });
  }

  private toggleTopNotification(filterRendered: FilterRendered, forwards: boolean) {
    if(!forwards && !filterRendered.topNotification) {
      return;
    }

    let isMounted = true;
    if(forwards) {
      isMounted = !!filterRendered.topNotificationContainer.parentElement;
      if(!isMounted) {
        filterRendered.scrollable.prepend(filterRendered.topNotificationContainer);
      }
    }

    SetTransition({
      element: filterRendered.topNotificationContainer,
      className: 'is-visible',
      duration: 250,
      forwards,
      useRafs: isMounted ? 0 : 2,
      onTransitionEnd: forwards ? undefined : () => {
        filterRendered.topNotification.dispose();
        filterRendered.topNotificationContainer.remove();
        filterRendered.topNotification =
          filterRendered.topNotificationContainer =
          filterRendered.topNotificationData = undefined;
      }
    });
  }

  private addFilter(filter: Pick<MyDialogFilter, 'title' | 'id' | 'localId'>) {
    const {id} = filter;
    if(id === FOLDER_ID_ARCHIVE) {
      return;
    }

    const renderedFilter = this.filtersRendered[id];
    if(renderedFilter) {
      positionElementByIndex(renderedFilter.container, this.folders.container, filter.localId);
      return;
    }

    const {ul, scrollable} = this.l(filter);
    scrollable.container.classList.add('tabs-tab', 'chatlist-parts', 'folders-scrollable');
    scrollable.attachBorderListeners();

    /* const parts = document.createElement('div');
    parts.classList.add('chatlist-parts'); */

    const top = document.createElement('div');
    top.classList.add('chatlist-top');

    const bottom = document.createElement('div');
    bottom.classList.add('chatlist-bottom');

    top.append(ul);
    scrollable.append(top, bottom);
    /* parts.append(top, bottom);
    scrollable.container.append(parts); */

    const div = scrollable.container;
    // this.folders.container.append(div);
    positionElementByIndex(scrollable.container, this.folders.container, filter.localId);

    this.filtersRendered[id] = {
      id,
      container: div,
      scrollable
    };

    this.onFiltersLengthChange();
  }

  private changeFiltersAllChatsKey() {
    const scrollable = this.folders.menuScrollContainer.firstElementChild;
    const key: LangPackKey = scrollable.scrollWidth > scrollable.clientWidth ? 'FilterAllChatsShort' : 'FilterAllChats';
    this.allChatsIntlElement.compareAndUpdate({key});
  }

  private onFiltersLengthChange() {
    let promise = this.showFiltersPromise;
    return promise ??= this.showFiltersPromise = pause(0).then(() => {
      if(this.showFiltersPromise !== promise) {
        return;
      }

      const length = Object.keys(this.filtersRendered).length;
      const show = length > 1;
      const wasShowing = !this.folders.menuScrollContainer.classList.contains('hide');

      if(show !== wasShowing) {
        this.folders.menuScrollContainer.classList.toggle('hide', !show);
        this.folders.menuGradient.classList.toggle('hide', !show);
        this.chatsContainer.classList.toggle('has-filters', show);
      }

      const [, setHasFolders] = useHasFolders();
      setHasFolders(show);

      this.changeFiltersAllChatsKey();

      this.showFiltersPromise = undefined;
    });
  }

  private generateEmptyPlaceholder(options: {
    title: LangPackKey,
    subtitle?: LangPackKey,
    subtitleArgs?: FormatterArguments,
    classNameType: string
  }) {
    const BASE_CLASS = 'empty-placeholder';
    const container = document.createElement('div');
    container.classList.add(BASE_CLASS, BASE_CLASS + '-' + options.classNameType);

    const header = document.createElement('div');
    header.classList.add(BASE_CLASS + '-header');
    _i18n(header, options.title);

    const subtitle = document.createElement('div');
    subtitle.classList.add(BASE_CLASS + '-subtitle');
    if(options.subtitle) {
      _i18n(subtitle, options.subtitle, options.subtitleArgs);
    }

    container.append(header, subtitle);

    return {container, header, subtitle};
  }

  private checkIfPlaceholderNeeded() {
    if(this.filterId === FOLDER_ID_ARCHIVE) {
      return;
    }

    const chatList = this.chatList;
    const part = chatList.parentElement as HTMLElement;
    let placeholderContainer = (Array.from(part.children) as HTMLElement[]).find((el) => el.matches('.empty-placeholder'));
    // const needPlaceholder = this.xd.scrollable.loadedAll.bottom && !chatList.childElementCount || true;
    const needPlaceholder = !this.xd.sortedList.itemsLength();
    // chatList.style.display = 'none';

    if(needPlaceholder && placeholderContainer) {
      return;
    } else if(!needPlaceholder) {
      if(placeholderContainer) {
        part.classList.remove('with-placeholder');
        placeholderContainer.remove();
      }

      return;
    }

    let placeholder: ReturnType<AppDialogsManager['generateEmptyPlaceholder']>, type: 'dialogs' | 'folder';
    if(!this.filterId) {
      placeholder = this.generateEmptyPlaceholder({
        title: 'ChatList.Main.EmptyPlaceholder.Title',
        classNameType: type = 'dialogs'
      });

      placeholderContainer = placeholder.container;

      const img = document.createElement('img');
      img.classList.add('empty-placeholder-dialogs-icon');

      this.emptyDialogsPlaceholderSubtitle = new I18n.IntlElement({
        element: placeholder.subtitle
      });

      Promise.all([
        this.updateContactsLength(false),
        renderImageFromUrlPromise(img, 'assets/img/EmptyChats.svg'),
        fastRafPromise()
      ]).then(([usersLength]) => {
        placeholderContainer.classList.add('visible');
        part.classList.toggle('has-contacts', !!usersLength);
      });

      placeholderContainer.prepend(img);
    } else {
      placeholder = this.generateEmptyPlaceholder({
        title: 'FilterNoChatsToDisplay',
        subtitle: 'FilterNoChatsToDisplayInfo',
        classNameType: type = 'folder'
      });

      placeholderContainer = placeholder.container;

      const div = document.createElement('div');
      const emoji = '📂';
      const size = 128;
      wrapStickerEmoji({
        div,
        emoji: emoji,
        width: size,
        height: size
      });

      placeholderContainer.prepend(div);

      const button = Button('btn-primary btn-color-primary btn-control', {
        text: 'FilterHeaderEdit',
        icon: 'settings'
      });

      attachClickEvent(button, async() => {
        const filter = await this.managers.filtersStorage.getFilter(this.filterId);
        appSidebarLeft.createTab(AppEditFolderTab).open({...AppEditFolderTab.getInitArgs(), initFilter: filter});
      });

      placeholderContainer.append(button);
    }

    part.append(placeholderContainer);
    part.classList.add('with-placeholder');
    part.dataset.placeholderType = type;
  }

  private updateContactsLength(updatePartClassName: boolean) {
    return this.updateContactsLengthPromise ??= this.managers.appUsersManager.getContacts().then((users) => {
      const subtitle = this.emptyDialogsPlaceholderSubtitle;
      if(subtitle) {
        let key: LangPackKey, args: FormatterArguments;

        if(users.length/*  && false */) {
          key = 'ChatList.Main.EmptyPlaceholder.Subtitle';
          args = [i18n('Contacts.Count', [users.length])];
        } else {
          key = 'ChatList.Main.EmptyPlaceholder.SubtitleNoContacts';
          args = [];
        }

        subtitle.compareAndUpdate({
          key,
          args
        });
      }

      if(updatePartClassName) {
        const chatList = this.chatList;
        const part = chatList.parentElement as HTMLElement;
        part.classList.toggle('has-contacts', !!users.length);
      }

      this.updateContactsLengthPromise = undefined;

      return users.length;
    });
  }

  private removeContactsPlaceholder() {
    const parts = this.chatList.parentElement.parentElement;
    parts.classList.remove('with-contacts');
    this.contactsPlaceholder.element.remove();
    this.contactsPlaceholder.destroy();
    this.contactsPlaceholder = undefined;
  }

  /** The chat list scrolled to its end — render the next page of contacts, if they are shown. */
  public loadContacts = () => {
    this.contactsPlaceholder?.loadMore();
  };

  /** A dialog for this peer appeared or went away — the contacts below the chat list follow. */
  public processContact = (peerId: PeerId) => {
    this.contactsPlaceholder?.processContact(peerId);
  };

  private _onListLengthChange = () => {
    this.checkIfPlaceholderNeeded();

    if(this.filterId !== FOLDER_ID_ALL) return;

    // return;
    const count = this.xd?.sortedList.itemsLength() || 0;

    if(count >= MIN_DIALOGS_WITHOUT_CONTACTS) {
      if(this.contactsPlaceholder) {
        this.removeContactsPlaceholder();
      }

      return;
    } else if(this.contactsPlaceholder) return;

    const chatList = this.chatList;
    const parts = chatList.parentElement.parentElement;
    const bottom = chatList.parentElement.nextElementSibling as HTMLElement;
    parts.classList.add('with-contacts');

    this.contactsPlaceholder = createChatlistContacts({
      managers: this.managers,
      onLengthChange: () => this.updateContactsLength(true),
      attachToList: (list) => this.setListClickListener({list})
    });

    bottom.append(this.contactsPlaceholder.element);
  };

  public onSomeDrawerToggle?: () => void;

  public async toggleForumTab(newTab?: ForumTab, hideTab = this.forumTab) {
    if(!hideTab && !newTab) {
      return;
    }

    if(hideTab) {
      this.xd.getListElement(hideTab.peerId)
      ?.classList.remove('is-forum-open');
    }

    if(hideTab === newTab) {
      newTab = undefined;
    }

    if(newTab) {
      appSidebarLeft.closeSearch();
    }

    hideTab?.toggle(false);
    const promise = newTab?.toggle(true);
    if(hideTab === this.forumTab) {
      this.forumTab = newTab;
      this.onSomeDrawerToggle?.();
      this.onChatListNarrowChange();
    }

    if(newTab) {
      this.xd.getListElement(newTab.peerId)
      ?.classList.add('is-forum-open');

      appImManager.selectTab(APP_TABS.CHATLIST);
    }

    if(promise) {
      await promise;
    }

    if(newTab && !this.forumNavigationItem) {
      this.forumNavigationItem = {
        type: 'forum',
        onPop: () => {
          this.forumNavigationItem = undefined;
          this.toggleForumTab();
        }
      };

      appNavigationController.pushItem(this.forumNavigationItem);
    } else if(!newTab && this.forumNavigationItem) {
      appNavigationController.removeItem(this.forumNavigationItem);
      this.forumNavigationItem = undefined;
    }

    const forwards = !!newTab;
    const useRafs = promise ? 2 : undefined;
    this.xd.toggleAvatarUnreadBadges(forwards, useRafs);

    this.transitionDrawersParent(forwards, useRafs);
  }

  private transitionDrawersParent(forwards: boolean, useRafs?: number) {
    const deferred = deferredPromise<void>();
    const duration = 300;
    SetTransition({
      element: appSidebarLeft.sidebarEl,
      className: 'is-forum-visible',
      duration,
      forwards,
      useRafs,
      onTransitionEnd: () => {
        deferred.resolve();
      }
    });

    dispatchHeavyAnimationEvent(deferred, duration).then(() => deferred.resolve());
  }

  public hasForumOpenFor(peerId: PeerId) {
    return !!this.forumsTabs.get(peerId);
  }

  public toggleForumTabByPeerId(
    peerId: PeerId,
    show?: boolean,
    asInnerIfAsMessages?: boolean,
    ForumTabConstructorOverride?: typeof ForumTab
  ) {
    const previousPromise = this.forumTabByPeerIdPromises.get(peerId);
    const run = () => this.toggleForumTabByPeerIdInternal(
      peerId,
      show,
      asInnerIfAsMessages,
      ForumTabConstructorOverride
    );
    const promise = previousPromise ?
      previousPromise.then(run, run) :
      run();
    this.forumTabByPeerIdPromises.set(peerId, promise);

    const cleanup = () => {
      if(this.forumTabByPeerIdPromises.get(peerId) === promise) {
        this.forumTabByPeerIdPromises.delete(peerId);
      }
    };
    promise.then(cleanup, cleanup);
    return promise;
  }

  private async toggleForumTabByPeerIdInternal(
    peerId: PeerId,
    show?: boolean,
    asInnerIfAsMessages?: boolean,
    ForumTabConstructorOverride?: typeof ForumTab
  ) {
    if(peerId === rootScope.myId) {
      const tab = appSidebarLeft.getTab(AppSharedMediaTab);
      if(show === true || (show === undefined && !tab)) {
        AppSharedMediaTab.open(appSidebarLeft, peerId, true);
      } else {
        if(tab?.peerId === peerId) {
          tab.close();
        }

        appImManager.setPeer({peerId});
      }

      return;
    }

    const {managers} = this;
    const chat = !peerId.isUser() ?
      apiManagerProxy.getChat(peerId.toChatId()) :
      undefined;
    const community = isCommunityChat(chat) ? chat : undefined;
    const isCommunity = !!community;
    const canOpenCommunity = community?._ === 'communityForbidden' ||
      (
        community?._ === 'community' &&
        !community.pFlags.left
      );
    let ForumTabConstructor = ForumTabConstructorOverride;
    if(!ForumTabConstructor) {
      if(canOpenCommunity) {
        ({CommunityForumTab: ForumTabConstructor} = await import(
          '@components/forumTab/communityForumTab'
        ));
      } else {
        ForumTabConstructor = ForumTab.register.getEntry(peerId);
      }
    }
    if(!ForumTabConstructor) return;

    const dialog = await managers.dialogsStorage.getDialogOnly(peerId);
    const viewAsMessages = dialog && !!dialog.pFlags.view_forum_as_messages;
    if(viewAsMessages) {
      const isSamePeer = appImManager.chat?.peerId === peerId;
      appImManager[isSamePeer || !asInnerIfAsMessages ? 'setPeer' : 'setInnerPeer']({
        type: ChatType.Chat,
        peerId
      });
      return;
    }

    const history = appSidebarLeft.getHistory();
    const lastTab = history[history.length - 1];
    let forumTab: ForumTab;
    const asNavigationTab = shouldOpenForumAsNavigationTab({
      hasNavigationHistory: !!lastTab,
      isCommunity,
      isNarrowScreen: mediaSizes.isLessThanFloatingLeftSidebar
    });
    if(asNavigationTab) {
      const isSameTab = lastTab instanceof ForumTab &&
        lastTab.peerId === peerId;
      const existingTab = findForumTabByPeerId(history, peerId);
      if(show === false) {
        if(isSameTab) {
          lastTab.close();
        }
        return;
      }

      if(isSameTab) {
        appSidebarLeft.closeSearch();
        if(show) {
          shake(lastTab.container);
        }
        return;
      }

      if(existingTab) {
        if(!await appSidebarLeft.closeTabsUntilTab(existingTab)) {
          return;
        }

        appSidebarLeft.closeSearch();
        return;
      }

      forumTab = appSidebarLeft.createTab(ForumTabConstructor);
      appSidebarLeft.closeSearch();
      const openPromise = forumTab.open({peerId, managers});
      if(isCommunity && mediaSizes.isLessThanFloatingLeftSidebar) {
        appImManager.selectTab(APP_TABS.CHATLIST);
      }
      await openPromise;
      return;
    }

    forumTab = this.forumsTabs.get(peerId);
    const isSameTab = this.forumTab && this.forumTab === forumTab;
    show ??= !isSameTab;
    if(show === isSameTab) {
      if(show) {
        appSidebarLeft.closeSearch();
        shake(forumTab.container);
      }

      return;
    }

    if(show && !forumTab) {
      forumTab = new ForumTabConstructor(undefined);
      forumTab.init({peerId, managers});

      this.forumsTabs.set(peerId, forumTab);
      this.forumsSlider.append(forumTab.container);

      forumTab.managers = this.managers;
      forumTab.eventListener.addEventListener('destroy', () => {
        this.forumsTabs.delete(peerId);
      });
    }

    return this.toggleForumTab(forumTab);
  }

  public openDialogInNewTab(element: HTMLElement) {
    const peerId = element.dataset.peerId.toPeerId();
    const lastMsgId = +element.dataset.mid || undefined;
    const threadId = +element.dataset.threadId || undefined;

    const params = new URLSearchParams();
    params.set('p', '' + peerId);
    const communityId = +element.dataset.communityId || undefined;
    if(communityId && peerId === communityId.toChatId().toPeerId(true)) {
      params.set('community', '1');
    }
    if(lastMsgId) params.set('message', '' + lastMsgId);
    if(threadId) params.set('thread', '' + threadId);
    const url = `#/im?${params.toString()}`;
    window.open(url, '_blank');
  }

  public setListClickListener({
    list,
    onFound,
    withContext = false,
    withArchiveContext = false,
    autonomous = false,
    openInner = false,
    selection = this.selection
  }: {
    list: HTMLElement,
    onFound?: (target: HTMLElement) => void | boolean,
    withContext?: boolean,
    withArchiveContext?: boolean,
    autonomous?: boolean,
    openInner?: boolean,
    /** what selecting a row of THIS list means - a forum tab selects its topics, not chats */
    selection?: DialogsSelectionBase
  }) {
    SELECTION_BY_LIST.set(list, selection);

    let lastActiveListElement: HTMLElement;

    const setPeerFunc = (openInner ? appImManager.setInnerPeer : appImManager.setPeer).bind(appImManager);

    const findAvatarWithStories = (target: EventTarget) => {
      return (target as HTMLElement).closest('.avatar.has-stories') as HTMLElement;
    };

    const getOpenStoryCallback = (target: EventTarget) => {
      const avatar = findAvatarWithStories(target);

      if(avatar) return () => {
        appImManager.openStoriesFromAvatar(avatar);
      };

      const archiveAvatar = (target as HTMLElement).closest('.archive-dialog-with-stories') as HTMLElement;
      const archiveDialog = (target as HTMLElement).closest(archiveDialogTagName);

      if(archiveAvatar && archiveDialog instanceof ArchiveDialog) return () => {
        archiveDialog.controls?.openStory?.();
      };
    }

    const isOpeningStoriesDisabled = () => appSidebarLeft.isCollapsed() && !appSidebarLeft.hasSomethingOpenInside();

    let willOpenStory = false;

    const setWillOpenStory = (e: Event) => willOpenStory = !isOpeningStoriesDisabled() && !!getOpenStoryCallback(e.target);
    const isDialogListAction = (target: EventTarget) => {
      return !!(target as HTMLElement).closest?.('[data-dialog-list-action]');
    };

    list.dataset.autonomous = '' + +autonomous;

    // * While chats are being selected a press on a row can also be the start of a reorder of the
    // * pinned block (attachPinnedDialogsReorder), and a drag must not toggle the row it moves - so
    // * in that mode, and only there, the press is acted upon when it ends. The click that ends a
    // * real drag is swallowed by `Sortable`, which is what keeps the selection as it was.
    let pendingPress: () => void;
    const onPress = (e: MouseEvent) => {
      this.log('dialogs click list');
      const target = e.target as HTMLElement;

      const archiveElem = findUpTag(target, archiveDialogTagName);
      if(archiveElem) {
        appSidebarLeft.openArchiveTab();
        return;
      }

      const elem = findDialogListElement(target);

      if(!elem) {
        return;
      }

      const peerId = elem.dataset.peerId.toPeerId();
      const lastMsgId = +elem.dataset.mid || undefined;
      const threadId = +elem.dataset.threadId || undefined;
      const monoforumParentPeerId = +elem.dataset.monoforumParentPeerId || undefined;
      const searchQuery = lastMsgId ? elem.dataset.searchQuery : undefined;

      const openChat = () => setPeerFunc({
        peerId: monoforumParentPeerId || peerId,
        monoforumThreadId: monoforumParentPeerId ? peerId : undefined,
        lastMsgId,
        threadId: threadId,
        highlight: searchQuery ? {type: 'search', query: searchQuery} : undefined
      });

      if(selection.isSelecting && selection.canSelect(elem)) {
        selection.toggleByElement(elem);
        return;
      }

      const isSponsored = elem.dataset.sponsored === 'true';
      if(isSponsored) {
        const chip = elem.querySelector('.sponsored-peer-chip');
        // if click was inside chip, open menu
        const rect = chip.getBoundingClientRect();
        if(e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom) {
          if(!IS_TOUCH_SUPPORTED) {
            simulateClickEvent(chip as HTMLElement);
          }
          return;
        }
      }

      if(onFound?.(elem) === false) {
        return;
      }

      const community = !peerId.isUser() ?
        apiManagerProxy.getChat(peerId.toChatId()) :
        undefined;
      if(
        community?._ === 'community' &&
        !community.pFlags.left &&
        !threadId
      ) {
        if(e.ctrlKey || e.metaKey) {
          this.openDialogInNewTab(elem);
          cancelEvent(e);
          return;
        }

        const isFromRightSidebar = appSidebarRight.sidebarEl.contains(elem);
        this.toggleForumTabByPeerId(
          peerId,
          isFromRightSidebar ? true : undefined,
          false
        );
        return;
      }

      // Shift+click → floating chat preview (tdesktop-style). Bypasses chat selection and
      // forum-tab toggling. Ctrl/Cmd (new-tab) takes precedence and is handled below.
      // Snapshot the anchor *now* — virtual scroll reuses DOM nodes and may yank this
      // element offscreen by the time the popup constructor runs.
      if(e.shiftKey && !e.ctrlKey && !e.metaKey && !autonomous) {
        showChatPreviewPopup({
          peerId: monoforumParentPeerId || peerId,
          monoforumThreadId: monoforumParentPeerId ? peerId : undefined,
          threadId,
          lastMsgId,
          anchor: chatPreviewAnchorFromDialogRow(elem)
        });
        cancelEvent(e);
        return;
      }

      const peer = apiManagerProxy.getPeer(peerId);

      const linkedChat = peer?._ === 'channel' && peer?.pFlags?.monoforum && peer?.linked_monoforum_id ?
        apiManagerProxy.getChat(peer.linked_monoforum_id) :
        undefined;

      if(
        linkedChat?._ === 'channel' &&
        linkedChat?.admin_rights?.pFlags?.manage_direct_messages &&
        !elem.dataset.isAllChats &&
        !lastMsgId &&
        !e.shiftKey
      ) {
        this.toggleForumTabByPeerId(peerId).then(() => {
          if(appImManager.chat?.peerId?.toChatId() !== linkedChat?.id && !mediaSizes.isLessThanFloatingLeftSidebar) openChat();
        });
        return;
      }

      if(peer?._ === 'user' && peer?.pFlags?.bot_forum_view && !lastMsgId && !threadId && !elem.dataset.isAllChats && !e.shiftKey) {
        this.toggleForumTabByPeerId(peerId).then(() => {
          if(appImManager.chat?.peerId?.toUserId() !== peer.id && !mediaSizes.isLessThanFloatingLeftSidebar) openChat();
        });
        return;
      }

      const isForum = !!elem.querySelector('.is-forum');
      if(isForum && !e.shiftKey && !lastMsgId) {
        this.toggleForumTabByPeerId(peerId, undefined, false);
        return;
      }

      if(e.ctrlKey || e.metaKey) {
        // TODO: How about opening a monoforum in new tab?
        this.openDialogInNewTab(elem);
        cancelEvent(e);
        return;
      }

      if(autonomous) {
        const sameElement = lastActiveListElement === elem;
        if(lastActiveListElement && !sameElement) {
          this.setDialogActiveStatus(lastActiveListElement, false);
        }

        if(elem) {
          this.setDialogActiveStatus(elem, true);
          lastActiveListElement = elem;
          this.lastActiveElements.add(elem);
        }
      }

      if(
        (!threadId && !monoforumParentPeerId || lastMsgId) &&
        this.xd.sortedList.list === list &&
        this.xd !== this.xds[FOLDER_ID_ARCHIVE]
      ) {
        this.toggleForumTab();
      }

      openChat();
    };
    list.addEventListener('mousedown', (e) => {
      pendingPress = undefined;
      if(
        e.button !== 0 ||
        setWillOpenStory(e) ||
        isDialogListAction(e.target)
      ) {
        return;
      }

      // * `AppSelection` listens for the press itself - it is what turns a drag from here into a
      // * selection of the rows it crosses - so this only holds the row's own toggle back until the
      // * press ends, or the drag would toggle the row it started on
      const selectable = selection.isSelecting && findDialogListElement(e.target);
      if(selectable && selection.canSelect(selectable)) {
        pendingPress = () => onPress(e);
        return;
      }

      onPress(e);
    }, {capture: true});

    // cancel link click
    // ! do not change it to attachClickEvent
    list.addEventListener('click', (e) => {
      if(isDialogListAction(e.target)) {
        return;
      }

      // Native links activate with a click alone from a keyboard or assistive
      // technology. Pointer activation already ran on mousedown — and so did the
      // guard that lives in that listener rather than in `onPress`, so the story
      // check has to be repeated here. `e.button` is 0 for a keyboard click and
      // `isDialogListAction` is already ruled out above.
      if(Modes.a11y && e.detail === 0 && !setWillOpenStory(e)) {
        onPress(e);
      }

      if(e.button === 0) {
        cancelEvent(e);
      }

      const press = pendingPress;
      pendingPress = undefined;
      press?.();

      if(!willOpenStory || isOpeningStoriesDisabled()) return;

      const callback = getOpenStoryCallback(e.target);
      callback?.();
    }, {capture: true});

    if(withContext) {
      this.contextMenu.attach(list);
    }

    if(withArchiveContext) {
      createArchiveDialogContextMenu({
        element: list
      });
    }
  }

  public createChatList(options: {
    // avatarSize?: number,
    // handheldsSize?: number,
    // size?: number,
    new?: boolean,
    dialogSize?: number,
    ignoreClick?: boolean
  } = {}) {
    const list = document.createElement('ul');
    // Legacy layout host: its direct children are native links, not li elements.
    // Keep those links exposed without announcing an invalid list structure.
    list.setAttribute('role', 'presentation');
    list.classList.add('chatlist'/* ,
      'chatlist-avatar-' + (options.avatarSize || 54) *//* , 'chatlist-' + (options.size || 72) */);

    if(options.new) {
      list.classList.add('chatlist-new');
    }

    if(options.dialogSize) {
      list.classList.add('chatlist-' + options.dialogSize);
    }

    // if(options.ignoreClick) {
    //   list.classList.add('disable-hover');
    // }

    /* if(options.handheldsSize) {
      list.classList.add('chatlist-handhelds-' + options.handheldsSize);
    } */

    return list;
  }

  public setLastMessageN(options: Parameters<AppDialogsManager['setLastMessage']>[0]) {
    const promise = this.setLastMessage(options);
    return promise.catch((err: ApiError) => {
      if(err?.type !== 'MIDDLEWARE') {
        this.log.error('set last message error', err);
      }
    });
  }

  // what to show instead of the last message when there is none at all
  private getEmptySubtitle(peerId: PeerId, listEl: HTMLElement) {
    if(apiManagerProxy.isBotforum(peerId)) {
      return i18n('NoMessagesYet');
    }

    if(!listEl.dataset.communityDialog) {
      return;
    }

    // a community bot has no history until its chat is started
    const peer = apiManagerProxy.getPeer(peerId);
    return peer?._ === 'user' && peer.pFlags.bot ?
      getCommunityPeerSubtitle(peer) :
      undefined;
  }

  private getLastMessageForDialog(dialog: PossibleDialog, lastMessage?: Message.message | Message.messageService) {
    let draftMessage: MyDraftMessage;
    const {peerId, draft} = dialog as Dialog;
    const peer = apiManagerProxy.getPeer(peerId);
    const isMonoforumParent = peer?._ === 'channel' && peer.pFlags?.monoforum;

    if(!lastMessage) {
      if(
        draft?._ === 'draftMessage' && !isMonoforumParent && (
          !peerId.isAnyChat() ||
          isForumTopic(dialog) ||
          !apiManagerProxy.isForum(peerId)
        )
      ) {
        draftMessage = draft;
      }

      lastMessage = (dialog as Dialog).topMessage;
      if(lastMessage?.mid !== dialog.top_message) {
        const trueLastMessage = apiManagerProxy.getMessageByPeer((dialog as MonoforumDialog)?.parentPeerId || peerId, dialog.top_message);
        if(trueLastMessage && (trueLastMessage as Message.messageService).action?._ !== 'messageActionChannelJoined') {
          lastMessage = trueLastMessage;
        }
      }
    }

    return {lastMessage, draftMessage};
  }

  /**
   * Everything the subtitle is rendered from, flattened into a comparable string.
   * `textColor` is deliberately left out: `setDialogActiveStatus` recolors the
   * already rendered custom emoji itself, so becoming (in)active must not cost
   * a re-render.
   */
  private getLastMessageRenderKey(options: {
    peerId: PeerId,
    isSaved: boolean,
    lastMessage?: Message.message | Message.messageService,
    draftMessage?: MyDraftMessage,
    highlightWord?: string,
    noForwardIcon?: boolean,
    subtitlePeerId?: PeerId,
    isRestricted?: boolean,
    isSensitive?: boolean
  }) {
    const {lastMessage, draftMessage} = options;
    const message = lastMessage as Message.message;
    const media = lastMessage && getMediaFromMessage(lastMessage, true);
    const messageMedia = message?.media as MessageMedia.messageMediaPhoto | MessageMedia.messageMediaDocument;
    const draftContent = draftMessage && getMessageForReplyContent(draftMessage);

    return [
      options.peerId,
      options.isSaved,
      options.highlightWord,
      options.noForwardIcon,
      options.subtitlePeerId,
      options.isRestricted,
      options.isSensitive,
      draftMessage?.date,
      draftMessage?.message,
      draftContent?.text,
      draftContent?.entities && JSON.stringify(
        draftContent.entities,
        (_key, value) => typeof(value) === 'bigint' ? value.toString() : value
      ),
      lastMessage?._,
      lastMessage?.peerId,
      lastMessage?.mid,
      lastMessage?.date,
      message?.edit_date,
      message?.fromId,
      message?.fwdFromId,
      message?.reply_to?._,
      (media as Photo.photo | MyDocument)?.id,
      (media as MyDocument)?.type,
      messageMedia?.pFlags?.spoiler,
      (messageMedia as MessageMedia.messageMediaPhoto)?.ttl_seconds,
      message?.message
    ].join('\x01');
  }

  private async setLastMessage({
    dialog,
    lastMessage: _lastMessage,
    dialogElement,
    highlightWord,
    isBatch = false,
    setUnread = false,
    noForwardIcon,
    setMessageId = true,
    subtitlePeerId
  }: {
    dialog: PossibleDialog,
    lastMessage?: Message.message | Message.messageService,
    dialogElement?: DialogElement,
    highlightWord?: string,
    isBatch?: boolean,
    setUnread?: boolean,
    noForwardIcon?: boolean,
    setMessageId?: boolean,
    subtitlePeerId?: PeerId
  }) {
    if(!dialogElement) {
      dialogElement = this.xd.getDialogElement(dialog.peerId);

      if(!dialogElement) {
        return;
      }
    }

    const {dom} = dialogElement;
    const {peerId} = dialog;
    const isSaved = isSavedDialog(dialog);

    const {deferred: promise, middleware} = setPromiseMiddleware(dom, 'setLastMessagePromise');

    const {draftMessage, lastMessage} = this.getLastMessageForDialog(dialog, _lastMessage);

    const isSearch = setUnread !== null && !setUnread;
    // * do not uncomment `setUnread` - unsetTyping right after this call will interrupt setting unread badges
    if(/* setUnread */!isSearch) {
      this.setUnreadMessagesN({dialog, dialogElement, isBatch, setLastMessagePromise: promise});
    }

    const isRestricted = !!lastMessage && isMessageRestricted(lastMessage as Message.message);
    const isSensitive = !!lastMessage && isMessageSensitive(lastMessage as Message.message);

    const renderKey = this.getLastMessageRenderKey({
      peerId,
      isSaved,
      lastMessage,
      draftMessage,
      highlightWord,
      noForwardIcon,
      subtitlePeerId,
      isRestricted,
      isSensitive
    });
    // * the key is dropped for the whole render and only restored once the new
    // * subtitle is in the DOM, so a render interrupted by the middleware can
    // * never leave a stale key behind
    const previousRenderParts = dom.lastMessageRenderParts;
    const canSkipRender = dom.lastMessageRenderKey === renderKey &&
      isRenderedSubtitleIntact(dom);
    delete dom.lastMessageRenderKey;
    delete dom.lastMessageRenderParts;

    if(!lastMessage && !draftMessage && !subtitlePeerId/*  || (lastMessage._ === 'messageService' && !lastMessage.rReply) */) {
      // * not guarded by the key: the placeholder also depends on the peer being a
      // * botforum / community dialog, and rendering it costs a single element
      const emptySubtitle = this.getEmptySubtitle(peerId, dom.listEl);

      disposeTextHighlight(dom);
      dom.lastMessageSpan.replaceChildren(...(emptySubtitle ? [emptySubtitle] : []));
      dom.lastTimeSpan.replaceChildren();
      delete dom.listEl.dataset.mid;
      delete dom.listEl.dataset.searchQuery;

      promise.resolve();
      return;
    }

    // set it before content so won't have bug in appSearch
    if(isSearch && setMessageId && lastMessage) {
      dom.listEl.dataset.mid = '' + lastMessage.mid;

      const replyTo = lastMessage.reply_to as MessageReplyHeader.messageReplyHeader;
      if(replyTo?.pFlags?.forum_topic) {
        dom.listEl.dataset.threadId = '' + getMessageThreadId(lastMessage);
      }
    }

    // * the click on the row carries the query on to the chat, so the found text
    // * lights up in the bubble too
    if(highlightWord) dom.listEl.dataset.searchQuery = highlightWord;
    else delete dom.listEl.dataset.searchQuery;

    let renderedParts = canSkipRender ? previousRenderParts : undefined;

    /* if(!dom.lastMessageSpan.classList.contains('user-typing')) */ if(!canSkipRender) {
      let mediaContainer: HTMLElement;
      const mediaParts: (Promise<HTMLElement> | HTMLElement)[] = [];

      if(lastMessage && !draftMessage && !isRestricted) {
        const media = getMediaFromMessage(lastMessage, true);
        const videoTypes: Set<MyDocument['type']> = new Set(['video', 'gif', 'round']);
        if(media && (media._ === 'photo' || videoTypes.has(media.type)) && !(((lastMessage as Message.message).media) as MessageMedia.messageMediaPhoto)?.ttl_seconds) {
          const spoiler = ((lastMessage as Message.message).media as MessageMedia.messageMediaPhoto | MessageMedia.messageMediaDocument)?.pFlags?.spoiler;
          const size = choosePhotoSize(media, 20, 20);

          if(size._ !== 'photoSizeEmpty') {
            mediaContainer = document.createElement('div');
            mediaContainer.classList.add('dialog-subtitle-media');

            if((media as MyDocument).type === 'round') {
              mediaContainer.classList.add('is-round');
            }

            mediaParts.push(wrapPhoto({
              photo: media,
              message: lastMessage,
              container: mediaContainer,
              withoutPreloader: true,
              size
            }).then(() => {
              if(spoiler || isSensitive) {
                return wrapMediaSpoiler({
                  media: media,
                  width: 20,
                  height: 20,
                  multiply: 0.1,
                  middleware: this.stateMiddlewareHelper.get(),
                  animationGroup: 'none'
                }).then((el) => {
                  mediaContainer.append(el);
                  return mediaContainer;
                });
              }

              return mediaContainer
            }));

            if(videoTypes.has((media as MyDocument).type)) {
              const playIcon = Icon('play_filled', 'dialog-subtitle-media-play');
              mediaContainer.append(playIcon);
            }
          }
        }
      }

      const withoutMediaType = !!mediaContainer && !!(lastMessage as Message.message)?.message;
      const parts = await renderDialogSubtitleParts({
        peerId,
        isSaved,
        lastMessage,
        draftMessage,
        noForwardIcon,
        mediaParts,
        withoutMediaType,
        withoutMessageIcon: !!mediaContainer,
        prependPeerId: subtitlePeerId,
        middleware,
        messageRenderer: wrapMessageForReply,
        peerTitleRenderer: wrapPeerTitle,
        textColor: this.getTextColor(
          dom.listEl.classList.contains('active')
        )
      });
      dom.lastMessageSpan.classList.add('dialog-subtitle-parts');
      disposeTextHighlight(dom);
      dom.lastMessageSpan.replaceChildren(...parts);
      renderedParts = parts;

      // * the last part is the message text itself (the ones before are the sender, the
      // * draft label, media thumbs) — that is where the query was matched
      if(highlightWord && lastMessage) {
        dom.textHighlight = highlightText({
          container: parts[parts.length - 1],
          match: {type: 'search', query: highlightWord}
        });
      }
    }

    // * the label is relative to the current day, so it is refreshed even when
    // * the subtitle itself was left alone
    if(lastMessage || draftMessage/*  && lastMessage._ !== 'draftMessage' */) {
      const date = draftMessage ? Math.max(draftMessage.date, lastMessage?.date || 0) : lastMessage.date;
      replaceContent(dom.lastTimeSpan, formatDateAccordingToTodayNew(new Date(date * 1000)));
    } else dom.lastTimeSpan.textContent = '';

    dom.lastMessageRenderKey = renderKey;
    dom.lastMessageRenderParts = renderedParts;
    promise.resolve();
  }

  public setUnreadMessagesN(options: Parameters<AppDialogsManager['setUnreadMessages']>[0]) {
    return this.setUnreadMessages(options).catch(() => {});
  }

  private async setUnreadMessages({
    dialog,
    dialogElement,
    isBatch = false,
    setLastMessagePromise
  }: {
    dialog: PossibleDialog,
    dialogElement: DialogElement,
    isBatch?: boolean,
    setLastMessagePromise?: Promise<void>
  }) {
    const {dom} = dialogElement;
    if(!dom) {
      // this.log.error('setUnreadMessages no dom!', dialog);
      return;
    }

    const isTopic = isForumTopic(dialog);
    const isSaved = isSavedDialog(dialog);
    const isMonoforumThread = isMonoforumDialog(dialog);
    const isAllChats = !!dialogElement?.dom?.listEl?.dataset?.isAllChats;
    const {deferred, middleware} = setPromiseMiddleware(dom, 'setUnreadMessagePromise');

    const {peerId} = dialog;
    const promises = Promise.all([
      this.managers.appNotificationsManager.isPeerLocalMuted({peerId: peerId, respectType: true, threadId: isTopic ? dialog.id : undefined}),
      !isSaved ? this.getLastMessageForDialog(dialog) : undefined,
      isTopic || isSaved ? !!dialog.pFlags.pinned : this.managers.dialogsStorage.isDialogPinned(peerId, this.filterId),
      this.managers.appMessagesManager.isDialogUnread(dialog),
      peerId.isAnyChat() && !isTopic ? this.managers.acknowledged.dialogsStorage.getForumUnreadCount(peerId, true).then((result) => {
        if(result.cached) {
          return result.result;
        } else {
          result.result.then(() => {
            this.setUnreadMessagesN({dialog, dialogElement});
          });

          return {count: 0, hasUnmuted: false};
        }
      }).catch(() => undefined as {count: number, hasUnmuted: boolean}) : undefined
    ]);

    let [isMuted, m, isPinned, isDialogUnread, forumUnreadCount] = await middleware(promises);
    const {count: unreadTopicsCount, hasUnmuted: hasUnmutedTopic} = forumUnreadCount || {};

    const {draftMessage, lastMessage} = m || {};
    let setStatusMessage: MyMessage;
    if(!draftMessage && lastMessage && lastMessage.pFlags.out && lastMessage.peerId !== rootScope.myId) {
      setStatusMessage = lastMessage;
    }

    const unreadCount = unreadTopicsCount ?? (isSaved ? 0 : dialog.unread_count);
    if(unreadTopicsCount !== undefined) {
      isDialogUnread = !!unreadCount;
    }

    if(isTopic && !isDialogUnread) {
      isDialogUnread = !getServerMessageId(dialog.read_inbox_max_id);
    }

    if(isAllChats) {
      isDialogUnread = false;
    }

    // dom.messageEl.classList.toggle('has-badge', hasBadge);

    // * have to await all promises before modifying something

    if(setLastMessagePromise) {
      try {
        await middleware(setLastMessagePromise);
      } catch(err) {
        return;
      }
    }

    const transitionDuration = isBatch ? 0 : BADGE_TRANSITION_TIME;

    dom.listEl.classList.toggle('no-unmuted-topic', !isMuted && hasUnmutedTopic !== undefined && !hasUnmutedTopic);

    setSendingStatus(dom.statusSpan, isTopic && dialog.pFlags.closed ? 'premium_lock' : setStatusMessage, true);

    // if(isTopic) {
    //   dom.statusSpan.parentElement.classList.toggle('is-closed', !!dialog.pFlags.closed);
    // }

    const hasPinnedBadge = isPinned && !isMonoforumThread && !isAllChats;
    const hasUnreadBadge = isDialogUnread;
    const hasUnreadAvatarBadge = dialogElement.isMainList !== false &&
      this.xd !== this.xds[FOLDER_ID_ARCHIVE] &&
      !isTopic &&
      !isMonoforumThread &&
      !isAllChats &&
      this.isChatListNarrow() &&
      isDialogUnread;
    // * `unreadCount` counts the unread topics for a forum, so the mention state
    // * must be derived from it too — not from `dialog.unread_count`
    const {isMention, hasMentionsBadge} = getDialogMentionBadgeState({
      unreadCount,
      unreadMessagesCount: (dialog as Dialog | ForumTopic).unread_count,
      unreadMentionsCount: isSaved || isMonoforumThread ? 0 : dialog.unread_mentions_count,
      hasUnreadBadge
    });
    const hasReactionsBadge = isSaved ? false : !!dialog.unread_reactions_count;
    const hasPollVotesBadge = isSaved || isMonoforumThread ? false : !!(dialog as Dialog | ForumTopic).unread_poll_votes_count;
    let unreadBadgeText: string;
    if(hasUnreadBadge) {
      // dom.unreadMessagesSpan.innerText = '' + (unreadCount ? formatNumber(unreadCount, 1) : ' ');
      unreadBadgeText = isMention ? '@' : '' + (unreadCount ? formatNumber(unreadCount, 1) : ' ');
    }

    dialogElement.setBadgeState({
      muted: isMuted,
      pinned: hasPinnedBadge,
      unread: hasUnreadBadge,
      unreadText: unreadBadgeText,
      unreadMention: isMention,
      unreadAvatar: hasUnreadAvatarBadge,
      unreadAvatarText: unreadBadgeText || undefined,
      mentions: hasMentionsBadge,
      reactions: hasReactionsBadge,
      pollVotes: hasPollVotesBadge,
      transitionDuration
    });

    if(isTopic && dom.unreadBadge) {
      const notVisited = isDialogUnread && unreadBadgeText === ' ';
      dom.unreadBadge.classList.toggle('not-visited', notVisited);
    }

    // if(isPinned && !isUnread && !isMention) {
    //   dom.unreadBadge.classList.add('badge-icon', 'dialog-pinned-icon');
    //   dom.unreadBadge.replaceChildren(Icon('chatspinned'));
    // } else if(dom.unreadBadge) {
    //   dom.unreadBadge.classList.remove('badge-icon', 'dialog-pinned-icon');
    //   if(!unreadBadgeText) {
    //     dom.unreadBadge.replaceChildren();
    //   }
    // }

    deferred.resolve();
  }

  private async getDialog(dialog: AnyDialog | PeerId, {threadOrSavedId, monoforumParentPeerId}: GetDialogOptions = {}) {
    if(typeof(dialog) !== 'object') {
      let originalDialog: AnyDialog | MonoforumDialog;
      if(monoforumParentPeerId) {
        originalDialog = await this.managers.monoforumDialogsStorage.getDialogByParent(monoforumParentPeerId, dialog);

        if(!originalDialog) return {
          peerId: dialog || NULL_PEER_ID,
          pFlags: {}
        } as MonoforumDialog;
      } else if(threadOrSavedId) {
        if(dialog === rootScope.myId) {
          originalDialog = await this.managers.dialogsStorage.getAnyDialog(dialog, threadOrSavedId);
          if(!originalDialog) {
            const peerId = dialog || NULL_PEER_ID;
            return {
              peerId,
              pFlags: {}
            } as any as SavedDialog;
          }
        } else {
          originalDialog = await this.managers.dialogsStorage.getForumTopic(dialog, threadOrSavedId);
          if(!originalDialog) {
            const peerId = dialog || NULL_PEER_ID;
            return {
              peerId,
              pFlags: {}
            } as any as ForumTopic;
          }
        }
      } else {
        originalDialog = await this.managers.appMessagesManager.getDialogOnly(dialog);
        if(!originalDialog) {
          const peerId = dialog || NULL_PEER_ID;
          return {
            peerId,
            peer: await this.managers.appPeersManager.getOutputPeer(peerId),
            pFlags: {}
          } as any as Dialog;
        }
      }

      return originalDialog;
    }

    return dialog as AnyDialog;
  }

  public addListDialog(options: Parameters<AppDialogsManager['addDialogNew']>[0] & InitDialogAdditionalOptions) {
    options.autonomous = false;
    options.withStories = true;

    const ret = this.addDialogNew(options);

    if(ret) {
      const promise = this.initDialog(ret, options);
      options.loadPromises?.push(promise);
      options.onInitPromise?.(promise);
    }

    if(ret && this.lazyLoadQueue) {
      const div = ret.dom.listEl;
      const lazyLoadElement: Parameters<AppDialogsManager['lazyLoadQueue']['push']>[0] = {
        div,
        load: async() => {
          await pause(200);
          if(!this.lazyLoadQueue.intersector.isVisible(div)) {
            this.lazyLoadQueue.push(lazyLoadElement); // * to process again
            return;
          }

          const getHistoryOptions: RequestHistoryOptions = {
            peerId: options.monoforumParentPeerId || options.peerId,
            threadId: options.threadId,
            monoforumThreadId: options.monoforumParentPeerId ? options.peerId : undefined,
            limit: 50
          };

          const readMaxId = await this.managers.appMessagesManager.getReadMaxIdIfUnread(
            options.peerId,
            options.threadId
          );

          if(readMaxId) {
            const limit = Math.floor(getHistoryOptions.limit / 2);
            await this.managers.appMessagesManager.getHistory({
              ...getHistoryOptions,
              offsetId: readMaxId || undefined,
              backLimit: readMaxId ? limit : undefined,
              limit
            });
          }

          return this.managers.appMessagesManager.getHistory(getHistoryOptions);
        }
      };

      this.lazyLoadQueue.push(lazyLoadElement);
      ret.middlewareHelper.onDestroy(() => {
        this.lazyLoadQueue.delete(lazyLoadElement);
      });
    }

    return ret;
  }

  public initDialog(dialogElement: DialogElement, options: Parameters<AppDialogsManager['addDialogNew']>[0] & InitDialogAdditionalOptions) {
    const {peerId} = options;
    const getDialogPromise = this.getDialog(options.dialog || peerId, {
      threadOrSavedId: options.threadId,
      monoforumParentPeerId: options.monoforumParentPeerId
    });

    const promise = getDialogPromise.then((dialog) => {
      const promises: (Promise<any> | void)[] = [];
      const isUser = peerId.isUser();
      if(!isUser && isDialog(dialog)) {
        promises.push(this.xd.processDialogForCallStatus(peerId, dialogElement.dom));
      }

      if(peerId !== rootScope.myId && isUser) {
        promises.push(this.managers.appUsersManager.getUserStatus(peerId.toUserId()).then((status) => {
          if(status?._ === 'userStatusOnline') {
            this.xd.setOnlineStatus(dialogElement.dom.avatarEl.node, true);
          }
        }));
      }

      promises.push(this.setLastMessageN({
        dialog,
        dialogElement: dialogElement,
        isBatch: options.isBatch,
        lastMessage: options.lastMessage,
        setUnread: true
      }));

      const allPromise = Promise.all(promises);

      // * a row built while the peer is already typing gets no `peer_typings` event
      // * of its own, so the indicator has to be restored here — after the subtitle
      // * is rendered, and off the returned promise so it never delays the row
      allPromise.then(() => setDialogTyping({
        dom: dialogElement.dom,
        peerId,
        threadId: isForumTopic(dialog) ? dialog.id : undefined
      })).catch(noop);

      return allPromise;
    });

    return promise;
  }

  /**
   * use for rendering search result
   */
  public addDialogAndSetLastMessage(options: Omit<Parameters<AppDialogsManager['addDialogNew']>[0], 'dialog'> & {
    message: MyMessage,
    peerId: PeerId,
    query?: string
  }) {
    const {peerId, message, query} = options;
    const ret = this.addDialogNew({
      ...options,
      ...getMessageSenderPeerIdOrName(message),
      peerId
    });

    this.setLastMessageN({
      dialog: {_: 'dialog', peerId} as any,
      lastMessage: message,
      dialogElement: ret,
      highlightWord: query
    });

    if(message.peerId !== peerId) {
      ret.dom.listEl.dataset.peerId = '' + message.peerId;
    }

    return ret;
  }

  public addDialogNew(options: DialogElementOptions & {container?: HTMLElement | Scrollable | false, append?: boolean}) {
    const d = new DialogElement({
      autonomous: !!options.container,
      avatarSize: 'bigger',
      ...options
      // avatarSize: !options.avatarSize || options.avatarSize >= 54 ? 'bigger' : 'abitbigger',
    });
    (d.container as any).dialogElement = d;

    if(options.container) {
      const method = options.append === false ? 'prepend' : 'append';
      options.container[method](d.container);
    }

    return d;
    // return this.addDialog(options.peerId, options.container, options.rippleEnabled, options.onlyFirstName, options.meAsSaved, options.append, options.avatarSize, options.autonomous, options.lazyLoadQueue, options.loadPromises, options.fromName, options.noIcons);
  }

  private showPollCancelConfirmation(randomId: string) {
    const listenerSetter = new ListenerSetter;
    let popup: PopupPeerHandle;

    const onSent = () => {
      toastNew({
        langPackKey: 'CancelPollConfirm.Timeout'
      });
    }

    listenerSetter.add(rootScope)('message_sent', ({tempMessage}) => {
      if(tempMessage?.random_id === randomId) {
        popup?.hide();
        onSent();
      }
    });

    confirmationPopup({
      titleLangKey: 'CancelPollConfirm.Title',
      descriptionLangKey: 'CancelPollConfirm.Description',
      button: {
        langKey: 'CancelPollConfirm.Button'
      },
      onPopup: (p: PopupPeerHandle) => {
        popup = p;
      }
    }).then(() => {
      this.managers.appMessagesManager.cancelPendingMessage(randomId).then((wasCanceled) => {
        if(!wasCanceled) {
          onSent();
        }
      });
    }, noop).finally(() => {
      listenerSetter.removeAll();
    });
  }
}

const appDialogsManager = new AppDialogsManager();
MOUNT_CLASS_TO.appDialogsManager = appDialogsManager;
export default appDialogsManager;
